// src/components/ElaborazioneOffertaTab.js
// "📐 Elaborazione Offerte" — strumento di calcolo dell'offerta economica, fedele al metodo
// storico dell'azienda (foglio Excel Costi_Modulo_R0): 4 blocchi di costo (Materiali,
// Manodopera Officina, Manodopera Cantiere, Spese di Installazione) + tre margini separati
// e modificabili (Utile e Spese Generali su Materiali+Manodopera, Utile su Spese Vive solo
// sulle Spese di Installazione). Strumento distinto dall'Inoltro Costi al Commerciale —
// quello è un passaggio di mano rapido con un solo markup per voce, questo è il calcolo
// dettagliato dell'offerta vera, con margini a due velocità come nel metodo storico.
import React, { useState, useEffect, useMemo } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, getDoc, setDoc, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { prossimoProgressivo } from "../utils/contatoriUtils";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { Plus, Trash2, X, ChevronLeft, Search } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";
import { economiaSintetica, importoCommessa } from "../utils/economiaCommessa";
import { SelettoreSede } from "./SediCliente";
import { copiaLibera, descrizioneSede } from "../utils/sediCliente";

export const STATI_EO = {
  bozza:       { label: "Bozza",       bg: "#f3f4f6", col: "#6b7280" },
  finalizzata: { label: "Finalizzata", bg: "#d1fae5", col: "#065f46" },
  emessa:      { label: "Emessa",      bg: "#ede9fe", col: "#5b21b6" },
  annullata:   { label: "Annullata",   bg: "#fee2e2", col: "#991b1b" },
};

function fmt(n) {
  return (parseFloat(n)||0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── Calcolo totali, identico al metodo del foglio Excel di riferimento ──────────────────
export function calcolaTotaliEO(dati) {
  const { materiali=[], manoOfficina=[], manoCantiere=[], speseInst=[], percUtile=0, percSpeseGenerali=0, percUtileSpeseVive=0 } = dati;
  // Ore per giorno di manodopera — congelate sull'offerta stessa al momento della prima
  // compilazione (non lette in tempo reale dalle Impostazioni Aziendali), altrimenti
  // un'offerta già Finalizzata cambierebbe silenziosamente i propri totali se in futuro
  // l'organizzazione modifica le Ore Lavorative Standard: la finalizzazione deve restare
  // un blocco vero. Le offerte create prima che questo campo esistesse continuano a
  // usare 8, il valore che avevano sempre usato di fatto.
  const oreGiorno = parseFloat(dati.oreGiornoStandard) || 8;
  const totaleMateriali = materiali.reduce((s,m) => {
    const scontato = (parseFloat(m.prezzoListino)||0) * (1 - (parseFloat(m.scontoPerc)||0)/100);
    return s + scontato * (parseFloat(m.quantita)||0);
  }, 0);
  const rigaManodopera = r => (parseFloat(r.persone)||0) * (parseFloat(r.giorni)||0) * oreGiorno * (parseFloat(r.prezzoOrario)||0);
  const totaleOfficina = manoOfficina.reduce((s,r) => s + rigaManodopera(r), 0);
  const totaleCantiere = manoCantiere.reduce((s,r) => s + rigaManodopera(r), 0);
  const totaleManodopera = totaleOfficina + totaleCantiere;
  const totaleSpeseInst = speseInst.reduce((s,r) => s + (parseFloat(r.quantita)||0)*(parseFloat(r.prezzoUnitario)||0), 0);
  const totaleNetto = totaleMateriali + totaleManodopera + totaleSpeseInst;
  const baseMargine = totaleMateriali + totaleManodopera;
  const totaleUtile = baseMargine * (parseFloat(percUtile)||0)/100;
  const totaleSpeseGenerali = baseMargine * (parseFloat(percSpeseGenerali)||0)/100;
  const totaleUtileSpeseVive = totaleSpeseInst * (parseFloat(percUtileSpeseVive)||0)/100;
  const totaleMargini = totaleUtile + totaleSpeseGenerali + totaleUtileSpeseVive;
  const totaleGenerale = totaleNetto + totaleMargini;
  const qta = parseFloat(dati.quantitaLotti)||0;
  const prezzoUnitario = qta>0 ? totaleGenerale/qta : null;
  return { totaleMateriali, totaleOfficina, totaleCantiere, totaleManodopera, totaleSpeseInst,
    totaleNetto, totaleUtile, totaleSpeseGenerali, totaleUtileSpeseVive, totaleMargini, totaleGenerale, prezzoUnitario };
}

// ── Somiglianza testuale semplice, per il controllo di coerenza prezzi ──────────────────
// Non un algoritmo perfetto — un confronto per parole in comune, sufficiente a segnalare
// "hai già scritto qualcosa di molto simile", senza richiedere un catalogo lavorazioni da
// mantenere. Ignora parole troppo corte (articoli, preposizioni) per non generare falsi match.
// ── Descrizione offerta generata dalle lavorazioni reali, a righe ───────────────────────
// Una riga per categoria (materiali, manodopera, spese) — resta un punto di partenza,
// l'utente può sempre aggiungere, togliere o rifinire ogni riga a mano dopo averle generate.
function generaRigheDescrizione(materiali, manoOfficina, manoCantiere, speseInst) {
  const righe = [];
  const dm = materiali.map(m=>m.descrizione).filter(Boolean);
  if (dm.length) righe.push(`Fornitura di ${dm.join(", ")}`);
  const dman = [...manoOfficina, ...manoCantiere].map(m=>m.descrizione).filter(Boolean);
  if (dman.length) righe.push(`Manodopera per ${dman.join(", ")}`);
  const ds = speseInst.map(s=>s.descrizione).filter(Boolean);
  if (ds.length) righe.push(`Spese di installazione per ${ds.join(", ")}`);
  return righe;
}

function normalizza(testo) {
  return (testo||"").toLowerCase().replace(/[^a-zà-ù0-9\s]/gi," ").split(/\s+/).filter(w=>w.length>=4);
}
function somiglianza(a, b) {
  const wa = new Set(normalizza(a)), wb = new Set(normalizza(b));
  if (wa.size===0 || wb.size===0) return 0;
  let comuni = 0;
  wa.forEach(w => { if (wb.has(w)) comuni++; });
  return comuni / Math.min(wa.size, wb.size); // quota della frase più corta ritrovata nell'altra
}
const SOGLIA_SIMILE = 0.6;
const MESI_CONFRONTO = 6;

// Cerca, in tutte le elaborazioni dell'organizzazione (non solo dello stesso cliente),
// righe di manodopera con descrizione simile negli ultimi N mesi, e restituisce il
// ventaglio di prezzi orari trovati — solo un avviso, mai un automatismo sul prezzo.
function trovaSimili(descrizioneAttuale, elaborazioniTutte, idEscludi) {
  if (!descrizioneAttuale || descrizioneAttuale.trim().length<4) return null;
  const sogliaData = new Date(); sogliaData.setMonth(sogliaData.getMonth()-MESI_CONFRONTO);
  const trovate = [];
  elaborazioniTutte.forEach(eo => {
    if (eo.id===idEscludi) return;
    const dataEo = eo.dataEmissione ? new Date(eo.dataEmissione+"T00:00:00") : null;
    if (!dataEo || dataEo < sogliaData) return;
    [...(eo.manoOfficina||[]), ...(eo.manoCantiere||[])].forEach(r => {
      if (!r.descrizione || !r.prezzoOrario) return;
      if (somiglianza(descrizioneAttuale, r.descrizione) >= SOGLIA_SIMILE) {
        trovate.push({ prezzo: parseFloat(r.prezzoOrario), numero: eo.numero, cliente: eo.clienteNome, data: eo.dataEmissione });
      }
    });
  });
  if (trovate.length < 3) return null; // sotto le 3 occorrenze non è ancora un pattern, solo rumore
  const prezzi = trovate.map(t=>t.prezzo);
  return { occorrenze: trovate, min: Math.min(...prezzi), max: Math.max(...prezzi) };
}

// ── Riga tabella generica per i 4 blocchi ────────────────────────────────────────────────
function inp(w) { return { width:w||"100%", padding:"4px 6px", border:"1px solid #e5e7eb", borderRadius:5, fontSize:11.5, fontFamily:"'Inter',sans-serif", outline:"none", boxSizing:"border-box", color:"#111827" }; }
const num = { ...inp(), textAlign:"right" };
const cell = { padding:"4px 6px", borderBottom:"1px solid #f3f4f6", verticalAlign:"top" };
// Quantità, ore, persone, prezzi: non hanno mai senso negativi — bloccati alla fonte, non
// solo con l'attributo HTML "min" (che da solo non impedisce di scrivere "-5" a mano).
const nonNegativo = v => v==="" ? "" : String(Math.max(0, parseFloat(v)||0));

function RigaMateriale({ r, onChange, onDelete, onVerifica, listino }) {
  const scontato = (parseFloat(r.prezzoListino)||0) * (1 - (parseFloat(r.scontoPerc)||0)/100);
  const totale = scontato * (parseFloat(r.quantita)||0);
  const upd = (k,v)=>onChange({...r,[k]:v});
  const [showRicerca, setShowRicerca] = useState(false);
  const [testoRicerca, setTestoRicerca] = useState("");
  const risultati = testoRicerca.trim().length>=2 ? (listino||[]).filter(v=>{
    const t = testoRicerca.toLowerCase();
    return (v.descrizione||"").toLowerCase().includes(t) || (v.codiceArticolo||"").toLowerCase().includes(t);
  }).slice(0,20) : [];
  const prelevaDaListino = (v) => {
    onChange({...r, descrizione:v.descrizione, codice:v.codiceArticolo||"", prezzoListino:v.prezzoUnitario, scontoPerc:v.scontoAbituale||0});
    setShowRicerca(false); setTestoRicerca("");
  };
  return (
    <tr>
      <td style={{...cell,width:60}}><input className="eo-riga-costo" style={num} type="number" min="0" value={r.quantita||""} onChange={e=>upd("quantita",nonNegativo(e.target.value))} placeholder="Q.tà"/></td>
      <td style={{...cell,minWidth:180,position:"relative"}}>
        <div style={{display:"flex",gap:4}}>
          <input className="eo-riga-costo" style={inp()} value={r.descrizione||""} onChange={e=>upd("descrizione",e.target.value)} onBlur={()=>onVerifica&&onVerifica(r)} placeholder="Descrizione materiale"/>
          <button type="button" onClick={()=>setShowRicerca(v=>!v)} title="Cerca dal Listino Materiali"
            style={{flexShrink:0,padding:"0 7px",border:"1px solid #7c3aed",borderRadius:5,background:"#faf5ff",color:"#7c3aed",cursor:"pointer",fontSize:12}}>🔍</button>
        </div>
        {showRicerca && (
          <div style={{position:"absolute",zIndex:30,top:"100%",left:0,right:0,background:"#fff",border:"1px solid #e5e7eb",borderRadius:8,boxShadow:"0 4px 16px rgba(0,0,0,.12)",marginTop:2,maxHeight:260,overflowY:"auto"}}>
            <input autoFocus value={testoRicerca} onChange={e=>setTestoRicerca(e.target.value)} placeholder="Cerca per descrizione o codice..."
              style={{width:"100%",padding:"7px 10px",border:"none",borderBottom:"1px solid #f3f4f6",fontSize:12,outline:"none",boxSizing:"border-box"}}/>
            {testoRicerca.trim().length<2 ? (
              <div style={{padding:10,fontSize:11,color:"#9ca3af"}}>Scrivi almeno 2 lettere per cercare</div>
            ) : risultati.length===0 ? (
              <div style={{padding:10,fontSize:11,color:"#9ca3af"}}>Nessun articolo trovato nel listino</div>
            ) : risultati.map(v=>(
              <div key={v.id} onClick={()=>prelevaDaListino(v)}
                style={{padding:"7px 10px",cursor:"pointer",borderBottom:"1px solid #f9fafb",fontSize:12}}
                onMouseEnter={e=>e.currentTarget.style.background="#faf5ff"} onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                <div style={{fontWeight:600,color:"#111827"}}>{v.descrizione}</div>
                <div style={{fontSize:10.5,color:"#9ca3af"}}>€{fmt(v.prezzoUnitario)} {v.scontoAbituale>0?`· sconto ${v.scontoAbituale}%`:""} {v.fornitore?`· ${v.fornitore}`:""}</div>
              </div>
            ))}
          </div>
        )}
      </td>
      <td style={{...cell,width:100}}><input className="eo-riga-costo" style={inp()} value={r.codice||""} onChange={e=>upd("codice",e.target.value)} placeholder="Codice"/></td>
      <td style={{...cell,width:90}}><input className="eo-riga-costo" style={num} type="number" step="0.01" min="0" value={r.prezzoListino||""} onChange={e=>upd("prezzoListino",nonNegativo(e.target.value))} placeholder="Listino €"/></td>
      <td style={{...cell,width:70}}><input className="eo-riga-costo" style={num} type="number" step="0.1" min="0" value={r.scontoPerc||""} onChange={e=>upd("scontoPerc",nonNegativo(e.target.value))} placeholder="Sconto %"/></td>
      <td style={{...cell,width:90,textAlign:"right",fontWeight:600,color:"#6b7280"}}>{scontato>0?`€${fmt(scontato)}`:"—"}</td>
      <td style={{...cell,width:100,textAlign:"right",fontWeight:800,color:"#059669"}}>{totale>0?`€${fmt(totale)}`:"—"}</td>
      <td style={{...cell,width:30}}><button onClick={onDelete} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5"}}><Trash2 size={13}/></button></td>
    </tr>
  );
}

function RigaManodopera({ r, onChange, onDelete, onVerifica }) {
  const ore = (parseFloat(r.persone)||0) * (parseFloat(r.giorni)||0) * 8;
  const totale = ore * (parseFloat(r.prezzoOrario)||0);
  const upd = (k,v)=>onChange({...r,[k]:v});
  return (
    <tr>
      <td style={{...cell,minWidth:200}}>
        <input className="eo-riga-costo" style={inp()} value={r.descrizione||""} onChange={e=>upd("descrizione",e.target.value)} onBlur={()=>onVerifica&&onVerifica(r)} placeholder="Descrizione lavorazione"/>
      </td>
      <td style={{...cell,width:70}}><input className="eo-riga-costo" style={num} type="number" min="0" value={r.persone||""} onChange={e=>upd("persone",nonNegativo(e.target.value))} placeholder="Persone"/></td>
      <td style={{...cell,width:70}}><input className="eo-riga-costo" style={num} type="number" step="0.5" min="0" value={r.giorni||""} onChange={e=>upd("giorni",nonNegativo(e.target.value))} placeholder="Giorni"/></td>
      <td style={{...cell,width:70,textAlign:"right",color:"#6b7280"}}>{ore>0?ore:"—"}</td>
      <td style={{...cell,width:90}}><input className="eo-riga-costo" style={num} type="number" step="0.5" min="0" value={r.prezzoOrario||""} onChange={e=>upd("prezzoOrario",nonNegativo(e.target.value))} placeholder="€/ora"/></td>
      <td style={{...cell,width:100,textAlign:"right",fontWeight:800,color:"#059669"}}>{totale>0?`€${fmt(totale)}`:"—"}</td>
      <td style={{...cell,width:30}}><button onClick={onDelete} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5"}}><Trash2 size={13}/></button></td>
    </tr>
  );
}

function RigaSpesaInstallazione({ r, onChange, onDelete }) {
  const totale = (parseFloat(r.quantita)||0) * (parseFloat(r.prezzoUnitario)||0);
  const upd = (k,v)=>onChange({...r,[k]:v});
  return (
    <tr>
      <td style={{...cell,minWidth:220}}><input className="eo-riga-costo" style={inp()} value={r.descrizione||""} onChange={e=>upd("descrizione",e.target.value)} placeholder="Descrizione spesa (trasporto, nolo, ecc.)"/></td>
      <td style={{...cell,width:80}}><input className="eo-riga-costo" style={num} type="number" step="0.1" min="0" value={r.quantita||""} onChange={e=>upd("quantita",nonNegativo(e.target.value))} placeholder="Q.tà/gg"/></td>
      <td style={{...cell,width:100}}><input className="eo-riga-costo" style={num} type="number" step="0.01" min="0" value={r.prezzoUnitario||""} onChange={e=>upd("prezzoUnitario",nonNegativo(e.target.value))} placeholder="Prezzo un. €"/></td>
      <td style={{...cell,width:100,textAlign:"right",fontWeight:800,color:"#059669"}}>{totale>0?`€${fmt(totale)}`:"—"}</td>
      <td style={{...cell,width:30}}><button onClick={onDelete} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5"}}><Trash2 size={13}/></button></td>
    </tr>
  );
}

function BloccoTabella({ titolo, colore, colonne, righe, setRighe, RigaComp, nuovaRiga, onVerifica, extraProps }) {
  return (
    <div style={{ border:"1px solid #e5e7eb", borderRadius:10, marginBottom:14, overflow:"hidden" }}>
      <div style={{ padding:"8px 14px", background:colore, fontSize:12.5, fontWeight:700, color:"#fff" }}>{titolo}</div>
      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ background:"#f9fafb" }}>
              {colonne.map(c=><th key={c} style={{padding:"5px 6px",fontSize:9.5,fontWeight:700,color:"#374151",textTransform:"uppercase",textAlign:"left"}}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {righe.map((r,i)=>(
              <RigaComp key={i} r={r} onVerifica={onVerifica} {...extraProps}
                onChange={nr=>setRighe(rs=>rs.map((x,j)=>j===i?nr:x))}
                onDelete={()=>setRighe(rs=>rs.filter((_,j)=>j!==i))}/>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={()=>setRighe(rs=>[...rs,nuovaRiga()])}
        style={{display:"flex",alignItems:"center",gap:5,padding:"7px 14px",border:"none",background:"#f9fafb",color:"#7c3aed",fontSize:11.5,fontWeight:700,cursor:"pointer",width:"100%",justifyContent:"center",borderTop:"1px solid #f3f4f6"}}>
        <Plus size={12}/> Aggiungi riga
      </button>
    </div>
  );
}

// ── Avviso di coerenza prezzi ─────────────────────────────────────────────────────────────
function AvvisoSimili({ dati, onVaiAlConfronto }) {
  if (!dati) return null;
  return (
    <div style={{ background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:8, padding:"8px 12px", marginBottom:8, display:"flex", alignItems:"center", gap:10 }}>
      <span style={{fontSize:16,flexShrink:0}}>⚠️</span>
      <div style={{flex:1,fontSize:11.5,color:"#9a3412"}}>
        Hai scritto una voce simile <strong>{dati.occorrenze.length} volte</strong> negli ultimi {MESI_CONFRONTO} mesi, a prezzi tra <strong>€{fmt(dati.min)}/h</strong> e <strong>€{fmt(dati.max)}/h</strong> — vuoi controllare?
      </div>
      <button onClick={()=>onVaiAlConfronto(dati)}
        style={{flexShrink:0,fontSize:11,fontWeight:700,color:"#9a3412",background:"#fff",border:"1px solid #fdba74",borderRadius:6,padding:"4px 10px",cursor:"pointer"}}>
        Controlla →
      </button>
    </div>
  );
}

// ── Vista di confronto (elenco delle occorrenze simili trovate) ─────────────────────────
function VistaConfronto({ dati, onTorna }) {
  return (
    <div style={{ padding:20 }}>
      <button onClick={onTorna} style={{display:"flex",alignItems:"center",gap:5,background:"none",border:"none",color:"#7c3aed",fontSize:13,fontWeight:600,cursor:"pointer",marginBottom:14}}>
        <ChevronLeft size={15}/> Torna all'elaborazione
      </button>
      <h3 style={{fontSize:15,fontWeight:700,marginBottom:4}}>Voci simili trovate</h3>
      <p style={{fontSize:12,color:"#6b7280",marginBottom:14}}>Ultimi {MESI_CONFRONTO} mesi, su tutte le commesse dell'organizzazione — prezzi tra €{fmt(dati.min)}/h e €{fmt(dati.max)}/h.</p>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
        <thead><tr style={{background:"#1e1b4b"}}>
          {["Elaborazione","Cliente","Data","€/ora"].map(h=><th key={h} style={{padding:"7px 10px",color:"#fff",fontSize:10,textAlign:"left",fontWeight:700}}>{h}</th>)}
        </tr></thead>
        <tbody>
          {dati.occorrenze.map((o,i)=>(
            <tr key={i} style={{background:i%2===0?"#fff":"#fafafa",borderBottom:"1px solid #f3f4f6"}}>
              <td style={{padding:"7px 10px",fontFamily:"monospace",fontWeight:700,color:"#7c3aed"}}>{o.numero}</td>
              <td style={{padding:"7px 10px"}}>{o.cliente||"—"}</td>
              <td style={{padding:"7px 10px",color:"#6b7280"}}>{o.data?o.data.split("-").reverse().join("/"):"—"}</td>
              <td style={{padding:"7px 10px",fontWeight:800,color:"#059669"}}>€{fmt(o.prezzo)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Vista Emissione Offerta — il documento per il cliente, fedele al layout del modulo
// aziendale: intestazione, dati offerta, saluto, un'unica riga con il totale calcolato in
// Modelli predefiniti per le condizioni — un punto di partenza rapido, sempre modificabile
export const MODELLI_PAGAMENTO = [
  "30 gg data fattura","60 gg data fattura","90 gg data fattura",
  "50% all'ordine, saldo a fine lavori","30% all'ordine, 40% a metà lavori, saldo a fine lavori",
  "Pagamento a SAL (stati di avanzamento lavori)","Anticipo 30% - Saldo 70% a consegna",
  "Rimessa diretta a 30 gg","Bonifico anticipato a conferma d'ordine","RIBA 60 gg fine mese",
];
const MODELLI_FORNITURA = ["Consegna e posa in opera compresa nel prezzo","Consegna prevista entro 30 giorni lavorativi dalla conferma d'ordine","Fornitura franco cantiere, posa esclusa","Tempi di consegna da concordare in fase di conferma ordine"];
const MODELLI_ESCLUSIONI = ["Tutto quanto non espressamente indicato nella presente offerta","Opere edili e murarie","Allacciamenti e pratiche autorizzative agli enti competenti","Oneri della sicurezza, se non diversamente specificato"];

function SelettoreModello({ modelli, onScegli }) {
  return (
    <select value="" onChange={e=>{ if(e.target.value) onScegli(e.target.value); }}
      style={{fontSize:10,color:"#7c3aed",background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:6,padding:"2px 6px",cursor:"pointer"}}>
      <option value="">+ modello</option>
      {modelli.map(m=><option key={m} value={m}>{m}</option>)}
    </select>
  );
}

// Elaborazione, condizioni, totali con IVA, chiusura e footer con i contatti aziendali. ────
function VistaEmissione({ form, emissione, updEm, totali, orgInfo, numero, clienteSelezionato, onDescrizioneModificataAMano }) {
  const imponibile = totali.totaleGenerale; // preso interamente dall'Elaborazione, un'unica voce
  const ivaImporto = imponibile * (parseFloat(form.ivaPerc)||0)/100;
  const totaleOrdine = imponibile + ivaImporto;
  const dataOfferta = form.dataEmissione ? new Date(form.dataEmissione+"T00:00:00") : new Date();
  const validoFino = new Date(dataOfferta); validoFino.setDate(validoFino.getDate() + (parseInt(form.validitaGiorni)||30));

  const campo = { border:"1px solid #e5e7eb", borderRadius:6, padding:"4px 8px", fontSize:12, fontFamily:"'Inter',sans-serif", outline:"none", width:"100%" };
  const lbl = { fontSize:9.5, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:".03em" };
  const coloreDoc = orgInfo?.coloreDocumenti || "#1e1b4b";
  const posizioneLogo = orgInfo?.posizioneLogo || "sinistra";
  const blocLogo = orgInfo?.logo ? <img src={orgInfo.logo} style={{height:46, objectFit:"contain"}}/> : <div style={{fontSize:20,fontWeight:900,color:coloreDoc}}>◆ {orgInfo?.nome||orgInfo?.name||"La tua azienda"}</div>;
  const blocAzienda = (
    <div style={{textAlign: posizioneLogo==="centro"?"center":posizioneLogo==="destra"?"right":"left"}}>
      {blocLogo}
      <div style={{fontSize:11, color:"#6b7280", marginTop:6, lineHeight:1.6}}>
        {orgInfo?.indirizzo||""}{orgInfo?.piva && <><br/>P.IVA {orgInfo.piva}</>}
      </div>
    </div>
  );

  return (
    <div style={{flex:1, overflowY:"auto", padding:24, background:"#f3f4f6"}}>
      <div style={{maxWidth:840, margin:"0 auto", background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:"32px 40px", boxShadow:"0 1px 4px rgba(0,0,0,.04)"}}>

        {/* Intestazione: azienda + dati offerta — logo nella posizione scelta in Impostazioni Aziendali.
            Entrambi i blocchi restano sempre nello stesso punto del codice, solo l'ordine
            visivo cambia (proprietà CSS "order") — niente scambi nel flusso della pagina,
            che con contenuti di larghezza diversa si erano rivelati inaffidabili. */}
        <div style={{display:"flex", flexDirection: posizioneLogo==="centro"?"column":"row", justifyContent: posizioneLogo==="centro"?"center":"space-between", alignItems: posizioneLogo==="centro"?"center":"flex-start", borderBottom:`3px solid ${coloreDoc}`, paddingBottom:16, marginBottom:20, gap:20}}>
          <div style={{order: posizioneLogo==="destra"?2:1, flexShrink:0}}>{blocAzienda}</div>
          <div style={{order: posizioneLogo==="destra"?1:2, textAlign: posizioneLogo==="destra"?"left":"right", flexShrink:0}}>
            <div style={{fontSize:24, fontWeight:900, color:coloreDoc, marginBottom:8}}>Offerta</div>
            <table style={{fontSize:11.5, borderSpacing:"6px 3px", marginLeft:posizioneLogo==="destra"?0:"auto"}}><tbody>
              <tr><td style={{color:"#9ca3af", textAlign:posizioneLogo==="destra"?"left":"right"}}>Data offerta</td><td style={{fontWeight:700}}>{dataOfferta.toLocaleDateString("it-IT")}</td></tr>
              <tr><td style={{color:"#9ca3af", textAlign:posizioneLogo==="destra"?"left":"right"}}>Numero offerta</td><td style={{fontWeight:700, color:"#d97706", fontFamily:"monospace"}}>{numero||"— salva per assegnarlo —"}</td></tr>
              {emissione.numeroCliente && <tr><td style={{color:"#9ca3af", textAlign:posizioneLogo==="destra"?"left":"right"}}>Numero cliente</td><td style={{fontWeight:700,color:"#111827"}}>{emissione.numeroCliente}</td></tr>}
              <tr><td style={{color:"#9ca3af", textAlign:posizioneLogo==="destra"?"left":"right"}}>Valida fino al</td><td style={{fontWeight:700}}>{validoFino.toLocaleDateString("it-IT")}</td></tr>
              <tr><td style={{color:"#9ca3af", textAlign:posizioneLogo==="destra"?"left":"right"}}>Redatta da</td><td><input style={{...campo,width:140,padding:"2px 6px"}} value={emissione.redattaDa||""} onChange={e=>updEm("redattaDa",e.target.value)}/></td></tr>
              <tr><td style={{color:"#9ca3af", textAlign:posizioneLogo==="destra"?"left":"right"}}>Referente</td><td style={{fontWeight:700,color:form.referente?"#111827":"#d1d5db"}}>{form.referente||"— non indicato —"}</td></tr>
            </tbody></table>
          </div>
        </div>

        {/* Cliente */}
        <div style={{marginBottom:16}}>
          <div style={{fontSize:13, fontWeight:700, color:"#111827"}}>{form.clienteNome||"— Nessun cliente selezionato —"}</div>
          {clienteSelezionato?.indirizzo && <div style={{fontSize:11, color:"#6b7280", marginTop:2}}>{clienteSelezionato.indirizzo}</div>}
          {form.sitoCliente && <div style={{fontSize:11, color:"#7c3aed", fontWeight:600, marginTop:2}}>📍 Sito: {form.sitoCliente}</div>}
        </div>

        {/* Saluto + testo introduttivo */}
        <div style={{fontSize:12.5, marginBottom:6}}>Spett.le {form.referente||form.clienteNome||"Cliente"},</div>
        <textarea value={emissione.testoIntroduttivo||""} onChange={e=>updEm("testoIntroduttivo",e.target.value)} rows={3}
          style={{width:"100%",fontSize:12.5,lineHeight:1.5,border:"1px solid #e5e7eb",borderRadius:8,padding:"8px 10px",fontFamily:"'Inter',sans-serif",outline:"none",color:"#111827",resize:"vertical",marginBottom:8}}/>
        <div style={{fontSize:11.5,color:"#7c3aed",fontWeight:600,marginBottom:16}}>
          📅 La presente offerta è valida fino al {validoFino.toLocaleDateString("it-IT")}.
        </div>

        <table style={{width:"100%", borderCollapse:"collapse", marginTop:12, marginBottom:4}}>
          <thead><tr style={{background:coloreDoc}}>
            {["Pos","Descrizione","Totale"].map(h=>(
              <th key={h} style={{padding:"7px 8px", color:"#fff", fontSize:9.5, textAlign:"left", fontWeight:700, textTransform:"uppercase"}}>{h}</th>
            ))}
          </tr></thead>
          <tbody><tr style={{borderBottom:"1px solid #e5e7eb"}}>
            <td style={{padding:"8px", fontSize:12}}>10</td>
            <td style={{padding:"8px", maxWidth:500}}>
              <textarea value={emissione.descrizioneOfferta||""} onChange={e=>{updEm("descrizioneOfferta",e.target.value); onDescrizioneModificataAMano&&onDescrizioneModificataAMano();}} rows={2}
                placeholder="Es. Fornitura e posa in opera di impianto elettrico completo..."
                style={{width:"100%",fontSize:12,lineHeight:1.4,border:"1px solid #e5e7eb",borderRadius:6,padding:"5px 7px",fontFamily:"'Inter',sans-serif",outline:"none",color:"#111827",resize:"vertical"}}/>
            </td>
            <td style={{padding:"8px", fontSize:12, fontWeight:800, color:"#059669"}}>€ {fmt(imponibile)}</td>
          </tr></tbody>
        </table>

        {/* Condizioni */}
        <div style={{display:"grid",gridTemplateColumns:"1fr",gap:8,marginTop:16,marginBottom:16}}>
          <div><label style={lbl}>Condizioni di pagamento</label><div style={{marginTop:3,fontSize:12,color:form.condizioniPagamento?"#111827":"#d1d5db",fontWeight:600}}>{form.condizioniPagamento||"— non indicate —"}</div></div>
          <div><label style={lbl}>Condizioni di fornitura</label><input style={{...campo,marginTop:3}} value={emissione.condizioniFornitura||""} onChange={e=>updEm("condizioniFornitura",e.target.value)} placeholder="Es. consegna prevista entro 4 settimane dalla firma"/></div>
          <div><label style={lbl}>Esclusioni</label><input style={{...campo,marginTop:3}} value={emissione.esclusioni||""} onChange={e=>updEm("esclusioni",e.target.value)} placeholder="Es. tutto quanto non esplicitamente indicato"/></div>
        </div>

        {/* Totali */}
        <div style={{marginLeft:"auto", width:280, background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:10, padding:"12px 16px"}}>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}><span>Totale imponibile</span><strong>€ {fmt(imponibile)}</strong></div>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:8}}><span>IVA</span><strong>€ {fmt(ivaImporto)}</strong></div>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:14,fontWeight:800,color:"#059669",borderTop:`2px solid ${coloreDoc}`,paddingTop:8}}><span>Totale ordine</span><span>€ {fmt(totaleOrdine)}</span></div>
        </div>

        <div style={{fontSize:12,marginTop:24}}>Per eventuali domande rimaniamo a Sua completa disposizione.</div>
        <div style={{fontSize:12,marginTop:16}}>Cordiali Saluti,</div>

        <div style={{display:"flex",justifyContent:"space-between",marginTop:40,fontSize:11,color:"#6b7280"}}>
          <div>Data: _______________________</div>
          <div>Timbro e firma per accettazione: _______________________</div>
        </div>

        {/* Footer azienda */}
        <div style={{display:"flex",justifyContent:"space-between",borderTop:"1px solid #e5e7eb",marginTop:28,paddingTop:14,fontSize:10.5,color:"#6b7280"}}>
          <div>
            <strong style={{color:"#111827"}}>{orgInfo?.nome||orgInfo?.name}</strong><br/>
            {orgInfo?.indirizzo}
          </div>
          <div style={{textAlign:"right"}}>
            {emissione.redattaDa}<br/>
            {orgInfo?.telefono && `Tel. ${orgInfo.telefono}`}<br/>
            {orgInfo?.email}
          </div>
        </div>
        {orgInfo?.piePagina && (
          <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid #f3f4f6",fontSize:9.5,color:"#9ca3af",textAlign:"center",whiteSpace:"pre-line"}}>
            {orgInfo.piePagina}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Pannello Riepilogo Economico ─────────────────────────────────────────────────────────
// Sempre visibile a destra mentre si compila l'elaborazione — nessun calcolo mentale, il
// preventivista vede in ogni momento dove sta andando il prezzo. Tre livelli di peso visivo:
// le singole voci di costo (normali), il Totale Costi Diretti (prima soglia, leggero risalto),
// e il Totale Elaborazione (l'unico vero elemento ad alto contrasto della pagina).
function RiepilogoEconomicoPanel({ totali, percUtile, percSpeseGenerali, percUtileSpeseVive, commessaSuggerita, onCollegaCommessa, projectNomeCollegato, datiConsuntivo, finalizzataBloccata, onCreaCommessa, creandoCliente }) {
  const Riga = ({ label, val, sub }) => (
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",padding:"5px 0"}}>
      <span style={{fontSize:12,color:"#4b5563"}}>{label}{sub && <span style={{fontSize:9.5,color:"#9ca3af"}}> {sub}</span>}</span>
      <span style={{fontSize:12.5,fontWeight:600,color:"#1f2937",fontFamily:"'SF Mono','Roboto Mono',monospace"}}>€ {fmt(val)}</span>
    </div>
  );
  return (
    <div style={{width:300,flexShrink:0,borderLeft:"1px solid #e5e7eb",background:"linear-gradient(180deg,#fafafa,#f5f5f7)",padding:"18px 20px",overflowY:"auto"}}>
      <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:16}}>
        <span style={{fontSize:16}}>📊</span>
        <span style={{fontSize:12,fontWeight:800,color:"#1e1b4b",textTransform:"uppercase",letterSpacing:".06em"}}>Riepilogo Economico</span>
      </div>

      {/* Costi diretti */}
      <div style={{background:"#fff",border:"1px solid #ececef",borderRadius:12,padding:"12px 14px",marginBottom:10}}>
        <Riga label="📦 Materiali" val={totali.totaleMateriali}/>
        <Riga label="🔧 Manod. Officina" val={totali.totaleOfficina}/>
        <Riga label="👷 Manod. Cantiere" val={totali.totaleCantiere}/>
        <Riga label="🚚 Spese Installazione" val={totali.totaleSpeseInst}/>
        <div style={{borderTop:"1px dashed #d1d5db",marginTop:8,paddingTop:8,display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
          <span style={{fontSize:12,fontWeight:700,color:"#374151"}}>Totale Costi Diretti</span>
          <span style={{fontSize:14,fontWeight:800,color:"#111827",fontFamily:"'SF Mono','Roboto Mono',monospace"}}>€ {fmt(totali.totaleNetto)}</span>
        </div>
      </div>

      {/* Margini */}
      <div style={{background:"#fff",border:"1px solid #ececef",borderRadius:12,padding:"12px 14px",marginBottom:14}}>
        <Riga label="Utile" val={totali.totaleUtile} sub={`(${percUtile||0}%)`}/>
        <Riga label="Spese Generali" val={totali.totaleSpeseGenerali} sub={`(${percSpeseGenerali||0}%)`}/>
        <Riga label="Margine Spese Vive" val={totali.totaleUtileSpeseVive} sub={`(${percUtileSpeseVive||0}%)`}/>
        <div style={{borderTop:"1px dashed #d1d5db",marginTop:8,paddingTop:8,display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
          <span style={{fontSize:12,fontWeight:700,color:"#92400e"}}>Totale Margini</span>
          <span style={{fontSize:14,fontWeight:800,color:"#92400e",fontFamily:"'SF Mono','Roboto Mono',monospace"}}>€ {fmt(totali.totaleMargini)}</span>
        </div>
      </div>

      {/* Totale elaborazione — l'unico elemento a vero alto contrasto della pagina */}
      <div style={{background:"linear-gradient(135deg,#059669,#047857)",borderRadius:14,padding:"18px 16px",boxShadow:"0 6px 20px rgba(5,150,105,.28)"}}>
        <div style={{fontSize:10.5,fontWeight:700,color:"rgba(255,255,255,.78)",textTransform:"uppercase",letterSpacing:".08em",marginBottom:4}}>Totale Elaborazione</div>
        <div style={{fontSize:28,fontWeight:900,color:"#fff",fontFamily:"'SF Mono','Roboto Mono',monospace",lineHeight:1.1}}>€ {fmt(totali.totaleGenerale)}</div>
        {totali.prezzoUnitario!==null && (
          <div style={{fontSize:11,color:"rgba(255,255,255,.85)",marginTop:8,paddingTop:8,borderTop:"1px solid rgba(255,255,255,.25)"}}>
            Prezzo unitario &nbsp;<strong>€ {fmt(totali.prezzoUnitario)}</strong>
          </div>
        )}
      </div>

      {totali.totaleGenerale>0 && (totali.totaleMargini/totali.totaleGenerale*100)<10 && (
        <div style={{marginTop:10,background:"#fef2f2",border:"1px solid #fecaca",borderRadius:10,padding:"9px 12px",fontSize:11,color:"#991b1b"}}>
          ⚠️ Margine sotto il 10% ({fmt(totali.totaleMargini/totali.totaleGenerale*100)}%) — controlla che sia voluto.
        </div>
      )}

      {/* Suggerimento automatico: propone il collegamento solo se la corrispondenza è chiara */}
      {commessaSuggerita && (
        <div style={{marginTop:10,background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:10,padding:"10px 12px",fontSize:11.5,color:"#5b21b6"}}>
          📎 Sembra collegata alla commessa <strong>"{commessaSuggerita.name}"</strong> — è questa?
          <div style={{fontSize:10,color:"#7c3aed",marginTop:4,lineHeight:1.5}}>
            {commessaSuggerita.codiceCommessa && <>Codice {commessaSuggerita.codiceCommessa} · </>}
            {commessaSuggerita.dataCreazione && <>creata il {commessaSuggerita.dataCreazione.toLocaleDateString("it-IT")} · </>}
            importo commessa €{fmt(commessaSuggerita.importoBudget)} (offerta: €{fmt(totali.totaleGenerale)})
          </div>
          <div style={{display:"flex",gap:6,marginTop:8}}>
            <button onClick={()=>onCollegaCommessa(commessaSuggerita)}
              style={{flex:1,padding:"5px",borderRadius:6,border:"none",background:"#7c3aed",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer"}}>
              Sì, collega
            </button>
            <button onClick={()=>onCollegaCommessa(null)}
              style={{flex:1,padding:"5px",borderRadius:6,border:"1px solid #e9d5ff",background:"#fff",color:"#5b21b6",fontSize:11,fontWeight:700,cursor:"pointer"}}>
              No
            </button>
          </div>
        </div>
      )}

      {finalizzataBloccata && !projectNomeCollegato && !commessaSuggerita && (
        <div style={{marginTop:10,background:"#fff",border:"1px dashed #d1d5db",borderRadius:10,padding:"10px 12px",textAlign:"center"}}>
          <div style={{fontSize:11,color:"#6b7280",marginBottom:8}}>Nessuna commessa collegata a questa offerta</div>
          <button onClick={onCreaCommessa} disabled={creandoCliente}
            style={{padding:"6px 14px",borderRadius:7,border:"none",background:"#7c3aed",color:"#fff",fontSize:11.5,fontWeight:700,cursor:"pointer"}}>
            {creandoCliente?"...":"🏗️ Crea commessa da questa offerta"}
          </button>
        </div>
      )}

      {/* Confronto margine previsto/reale — solo quando la commessa è collegata */}
      {projectNomeCollegato && (
        <div style={{marginTop:10,background:"#fff",border:"1px solid #ececef",borderRadius:12,padding:"12px 14px"}}>
          <div style={{fontSize:10.5,fontWeight:700,color:"#5b21b6",marginBottom:2,textTransform:"uppercase",letterSpacing:".03em"}}>📊 Previsto vs Reale</div>
          <div style={{fontSize:10,color:"#9ca3af",marginBottom:8}}>Commessa collegata: {projectNomeCollegato}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            <div>
              <div style={{fontSize:9,color:"#9ca3af",textTransform:"uppercase"}}>Previsto</div>
              <div style={{fontSize:15,fontWeight:800,color:"#7c3aed"}}>{totali.totaleGenerale>0?fmt(totali.totaleMargini/totali.totaleGenerale*100):"0"}%</div>
            </div>
            <div>
              <div style={{fontSize:9,color:"#9ca3af",textTransform:"uppercase"}}>Reale</div>
              <div style={{fontSize:15,fontWeight:800,color:!datiConsuntivo||datiConsuntivo.marginePerc===null?"#9ca3af":datiConsuntivo.marginePerc<0?"#dc2626":"#059669"}}>
                {!datiConsuntivo||datiConsuntivo.marginePerc===null?"—":`${fmt(datiConsuntivo.marginePerc)}%`}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════════════════
export function EOForm({ eo, orgId, orgInfo, onClose, soloLettura, onNavigate }) {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const [form, setForm] = useState(eo || {
    oggetto: "", dataEmissione: toLocalDateStr(new Date()), stato: "bozza",
    clienteId: "", clienteNome: "", note: "", quantitaLotti: "", descrizioneOfferta: "", referente: "", condizioniPagamento: "", ivaPerc: 22, validitaGiorni: 30, sitoCliente: "",
  });
  const [clienti, setClienti] = useState([]);
  const [listinoMateriali, setListinoMateriali] = useState([]);
  const [nuovoClienteGenerico, setNuovoClienteGenerico] = useState(!eo?.clienteId && !!eo?.clienteNome);
  const [vista, setVista] = useState("elaborazione"); // "elaborazione" | "emissione"
  const clienteSelezionato = useMemo(()=>clienti.find(c=>c.id===form.clienteId)||null, [clienti, form.clienteId]);

  // Storico margine del cliente selezionato — quanto in media il margine reale finisce
  // sotto quello previsto, sulle sue offerte precedenti già collegate a una commessa
  // conclusa. Serve storia vera (almeno 2 offerte), mai un numero inventato su un cliente
  // nuovo o senza precedenti.
  const [storicoMargineCliente, setStoricoMargineCliente] = useState(null);
  useEffect(() => {
    if (!form.clienteId || !orgId) { setStoricoMargineCliente(null); return; }
    (async () => {
      const snap = await getDocs(query(collection(db,"elaborazioniOfferte"),
        where("orgId","==",orgId), where("clienteId","==",form.clienteId)));
      const collegate = snap.docs.map(d=>({id:d.id,...d.data()})).filter(e=>e.projectIdCollegato && e.id!==eo?.id);
      if (collegate.length<2) { setStoricoMargineCliente(null); return; }

      const ordiniOrg = (await getDocs(query(collection(db,"ordiniAcquisto"), where("organizationId","==",orgId)))).docs.map(d=>d.data());
      const scostamenti = await Promise.all(collegate.map(async (e) => {
        const [budgetSnap, movSnap] = await Promise.all([
          getDocs(query(collection(db,"budgetCommessa"), where("projectId","==",e.projectIdCollegato), where("organizationId","==",orgId))),
          getDocs(query(collection(db,"movimentiEconomici"), where("projectId","==",e.projectIdCollegato), where("organizationId","==",orgId))),
        ]);
        // Stesse regole del Controllo Economico (utils/economiaCommessa.js)
        const { ricavi, costi } = economiaSintetica({ budget: budgetSnap.empty?null:budgetSnap.docs[0].data(), movimenti: movSnap.docs.map(d=>d.data()), ordini: ordiniOrg, projectId: e.projectIdCollegato });
        if (ricavi<=0) return null;
        const margineReale = (ricavi-costi)/ricavi*100;
        const totali = calcolaTotaliEO(e);
        const marginePrevisto = totali.totaleGenerale>0 ? (totali.totaleMargini/totali.totaleGenerale*100) : 0;
        return marginePrevisto - margineReale;
      }));
      const validi = scostamenti.filter(s=>s!==null);
      if (validi.length<2) { setStoricoMargineCliente(null); return; }
      const media = validi.reduce((s,v)=>s+v,0)/validi.length;
      setStoricoMargineCliente({ media, n: validi.length });
    })();
  }, [form.clienteId, orgId, eo?.id]);

  // Listino Materiali, per la ricerca rapida dentro le righe Materiali
  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(query(collection(db,"listinoMateriali"), where("organizationId","==",orgId)),
      snap => setListinoMateriali(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [orgId]);

  // Descrizione offerta a righe — se l'elaborazione è vecchia e aveva un unico blocco di
  // testo (prima di questa versione), lo recupero come prima riga, senza perdere nulla.
  const [righeDescrizione, setRigheDescrizioneState] = useState(() => {
    if (eo?.descrizioneRighe?.length) return eo.descrizioneRighe;
    if (eo?.descrizioneOfferta?.trim()) return [eo.descrizioneOfferta];
    return [];
  });
  const setRigheDescrizione = (nuove) => {
    setRigheDescrizioneState(nuove);
    upd("descrizioneOfferta", nuove.filter(r=>r.trim()).join("\n"));
  };

  const [materiali, setMateriali] = useState(eo?.materiali || []);
  const [manoOfficina, setManoOfficina] = useState(eo?.manoOfficina || []);
  const [manoCantiere, setManoCantiere] = useState(eo?.manoCantiere || []);
  const [speseInst, setSpeseInst] = useState(eo?.speseInst || []);
  const [percUtile, setPercUtile] = useState(eo?.percUtile ?? null);
  const [percSpeseGenerali, setPercSpeseGenerali] = useState(eo?.percSpeseGenerali ?? null);
  const [percUtileSpeseVive, setPercUtileSpeseVive] = useState(eo?.percUtileSpeseVive ?? null);
  const [emissione, setEmissione] = useState(eo?.emissione || {
    descrizioneOfferta: "", numeroCliente: "", referente: "",
    redattaDa: currentUser?.displayName||"",
    testoIntroduttivo: "RingraziandoLa per l'interesse dimostrato nei confronti della nostra azienda, siamo lieti di sottoporLe la nostra migliore offerta economica per la realizzazione delle attività di seguito descritte.",
    condizioniPagamento: "30 GG data fattura", condizioniFornitura: "", esclusioni: "",
    quantita: 1, unitaMisura: "a corpo",
  });
  const [saving, setSaving] = useState(false);
  const [elaborazioniTutte, setElaborazioniTutte] = useState([]);
  const [avviso, setAvviso] = useState(null);
  // Diventa true appena l'utente tocca a mano la descrizione dentro Emissione — da quel
  // momento la copia automatica dall'Elaborazione si ferma, per non sovrascrivere una
  // versione già personalizzata per il cliente.
  const [descrizioneModificataInEmissione, setDescrizioneModificataInEmissione] = useState(!!eo?.emissione?.descrizioneModificataInEmissione);
  const [confronto, setConfronto] = useState(null);

  const upd = (k,v) => setForm(f=>({...f,[k]:v}));
  const updEm = (k,v) => setEmissione(f=>({...f,[k]:v}));

  // Elenco clienti dell'organizzazione, per il menu a tendina
  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(query(collection(db,"clienti"), where("organizationId","==",orgId)),
      snap => setClienti(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.ragioneSociale||"").localeCompare(b.ragioneSociale||"","it"))));
  }, [orgId]);

  const [oreGiornoStandard, setOreGiornoStandard] = useState(eo?.oreGiornoStandard ?? null);

  // Default margini dalle Impostazioni Aziendali, solo se non già impostati (nuova elaborazione)
  useEffect(() => {
    if (eo || !orgId) return;
    getDoc(doc(db,"organizations",orgId)).then(snap => {
      if (!snap.exists()) return;
      const d = snap.data();
      setPercUtile(d.utileEODefault ?? 15);
      setPercSpeseGenerali(d.speseGeneraliEODefault ?? 10);
      setPercUtileSpeseVive(d.utileSpeseViveEODefault ?? 5);
      setOreGiornoStandard(d.oreLavorativeStandard ?? 8);
    });
  }, [orgId, eo]);

  // Copia automatica della descrizione dall'Elaborazione all'Emissione — si ferma da sola
  // appena l'utente la personalizza direttamente dentro Emissione.
  useEffect(() => {
    if (descrizioneModificataInEmissione) return;
    setEmissione(em => ({...em, descrizioneOfferta: form.descrizioneOfferta||""}));
  }, [form.descrizioneOfferta, descrizioneModificataInEmissione]);

  // Tutte le elaborazioni dell'organizzazione, per il controllo di coerenza prezzi —
  // il confronto guarda tutta l'azienda, non solo lo stesso cliente (come da indicazione).
  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(query(collection(db,"elaborazioniOfferte"), where("orgId","==",orgId)),
      snap => setElaborazioniTutte(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [orgId]);

  const verificaSimili = (riga) => {
    const trovate = trovaSimili(riga.descrizione, elaborazioniTutte, eo?.id);
    setAvviso(trovate);
  };

  const totali = useMemo(() => calcolaTotaliEO({
    materiali, manoOfficina, manoCantiere, speseInst,
    percUtile: percUtile||0, percSpeseGenerali: percSpeseGenerali||0, percUtileSpeseVive: percUtileSpeseVive||0,
    quantitaLotti: form.quantitaLotti, oreGiornoStandard,
  }), [materiali, manoOfficina, manoCantiere, speseInst, percUtile, percSpeseGenerali, percUtileSpeseVive, form.quantitaLotti, oreGiornoStandard]);

  // Permessi: solo chi ha creato l'elaborazione, o chi l'ha ricevuta in condivisione, può
  // modificarla. Una nuova elaborazione (non ancora salvata) è sempre modificabile da chi
  // la sta creando. Se non hai i permessi, il form si comporta come sola lettura da solo,
  // anche se non arrivi dal Registro con soloLettura esplicito.
  const puoModificare = !eo || eo.compilatoreUid===currentUser?.uid || (eo.condivisoCon||[]).includes(currentUser?.uid);
  const nonModificabilePerAltri = eo && !puoModificare;
  // Una volta salvata come Finalizzata, resta bloccata per sempre — anche per chi l'ha
  // creata. Non è reversibile dal menu a tendina, non è un errore da poter correggere:
  // è una scelta voluta, per dare certezza che un'offerta finalizzata non cambi più sotto
  // ai piedi di nessuno.
  const finalizzataBloccata = eo?.stato === "finalizzata";
  const soloLetturaEffettivo = soloLettura || nonModificabilePerAltri || finalizzataBloccata;
  const [showCondividi, setShowCondividi] = useState(false);
  const [utentiOrg, setUtentiOrg] = useState([]);

  // Suggerimento automatico della commessa collegata — solo quando la corrispondenza è
  // chiara e unica (stesso cliente, creata dopo l'offerta, importo vicino al totale offerto).
  // Se non è chiaro, non propone nulla: mai un collegamento sbagliato spacciato per giusto.
  const [commessaSuggerita, setCommessaSuggerita] = useState(null);
  const [suggerimentoRifiutato, setSuggerimentoRifiutato] = useState(false);
  const [datiConsuntivo, setDatiConsuntivo] = useState(null);

  useEffect(() => {
    if (!finalizzataBloccata || !eo?.clienteId || eo?.projectIdCollegato || !orgId || suggerimentoRifiutato) { setCommessaSuggerita(null); return; }
    (async () => {
      const candidateSnap = await getDocs(query(collection(db,"projects"),
        where("organizationId","==",orgId), where("clienteId","==",eo.clienteId)));
      const candidati = candidateSnap.docs.map(d=>({id:d.id,...d.data()}))
        .filter(p => !p.isParent && (!eo.dataEmissione || !p.createdAt || p.createdAt.toDate?.() >= new Date(eo.dataEmissione)));
      if (candidati.length===0) return;

      const budgetSnap = await getDocs(query(collection(db,"budgetCommessa"), where("organizationId","==",orgId)));
      const budgetPerProject = {};
      // W452: budget principale (il primo per identificativo) e importo letto anche se salvato come testo «20.000,00»
      budgetSnap.docs.forEach(d => { const pid = d.data().projectId; if (pid && budgetPerProject[pid] === undefined) budgetPerProject[pid] = importoCommessa(d.data()); });

      const totaleOfferta = totali.totaleGenerale;
      const corrispondenti = candidati.filter(p => {
        const importo = budgetPerProject[p.id];
        if (!importo || totaleOfferta<=0) return false;
        return Math.abs(importo-totaleOfferta)/totaleOfferta <= 0.20; // tolleranza ±20%
      });
      if (corrispondenti.length===1) {
        const p = corrispondenti[0];
        setCommessaSuggerita({ ...p, importoBudget: budgetPerProject[p.id], dataCreazione: p.createdAt?.toDate?.() || null });
      }
    })();
  }, [finalizzataBloccata, eo?.clienteId, eo?.projectIdCollegato, orgId, totali.totaleGenerale]);

  // Creazione rapida cliente — stesso schema di numerazione già usato in Anagrafica Clienti,
  // così un cliente nuovo ottiene subito il suo numero, senza dover aprire un'altra pagina.
  const [creandoCliente, setCreandoCliente] = useState(false);
  const creaClienteRapido = async () => {
    if (!form.clienteNome?.trim()) { addToast("Scrivi prima il nome del cliente","error"); return null; }
    setCreandoCliente(true);
    try {
      const progressivo = await prossimoProgressivo(`${orgId}_numeroCliente`, { orgId }, 99);
      const numeroCliente = `CLI-${String(progressivo).padStart(4,"0")}`;
      const nuovoRef = await addDoc(collection(db,"clienti"), {
        ragioneSociale: form.clienteNome.trim(), organizationId: orgId, numeroCliente,
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      setNuovoClienteGenerico(false);
      setForm(f=>({...f, clienteId:nuovoRef.id, clienteNome:form.clienteNome.trim()}));
      updEm("numeroCliente", numeroCliente);
      addToast(`Cliente creato — numero ${numeroCliente}`,"success");
      return nuovoRef.id;
    } finally {
      setCreandoCliente(false);
    }
  };

  // Crea la commessa direttamente da questa offerta Finalizzata — se il cliente è già in
  // anagrafica salta dritto al form della commessa (trova/crea da sola la sua cartella),
  // se è un cliente nuovo lo crea prima al volo, stesso bottone usato altrove nel form.
  const creaCommessaDaOfferta = async () => {
    let clienteId = form.clienteId;
    if (!clienteId) {
      clienteId = await creaClienteRapido();
      if (!clienteId) return;
    }
    onNavigate && onNavigate({
      page:"crea-commessa-da-offerta", clienteId, clienteNome: form.clienteNome,
      oggettoOfferta: form.oggetto, eoId: eo?.id, sitoCliente: form.sitoCliente || "", sede: form.sede || null,
    });
  };

  const collegaCommessa = async (progetto) => {
    if (!progetto) { setSuggerimentoRifiutato(true); setCommessaSuggerita(null); return; }
    if (!eo?.id) return;
    await updateDoc(doc(db,"elaborazioniOfferte",eo.id), {
      projectIdCollegato: progetto.id, projectNomeCollegato: progetto.name||"", updatedAt: serverTimestamp(),
    });
    setCommessaSuggerita(null);
    addToast("Commessa collegata ✅","success");
  };

  // Margine reale, letto da Controllo Economico della commessa collegata — stesso calcolo
  // già in uso lì (importo commessa − costi reali totali). Include anche i materiali da
  // NF/ODA inviate, esattamente come fa Controllo Economico: quei costi non sono mai
  // registrati come movimentiEconomici, quindi vanno sommati a parte per non escluderli.
  useEffect(() => {
    if (!eo?.projectIdCollegato) { setDatiConsuntivo(null); return; }
    (async () => {
      // Stesse regole del Controllo Economico e della Cabina di Regia (utils/economiaCommessa.js):
      // contratto + varianti + ricavi registrati; costi da movimenti, ordini (IVA esclusa) e
      // costi calcolati. Prima qui si sommavano anche le NF inviate, contate due volte con gli ordini.
      const [budgetSnap, movSnap, odaSnap, orgSnap] = await Promise.all([
        getDocs(query(collection(db,"budgetCommessa"), where("projectId","==",eo.projectIdCollegato), where("organizationId","==",orgId))),
        getDocs(query(collection(db,"movimentiEconomici"), where("projectId","==",eo.projectIdCollegato), where("organizationId","==",orgId))),
        getDocs(query(collection(db,"ordiniAcquisto"), where("organizationId","==",orgId))),
        getDoc(doc(db,"organizations",orgId)).catch(() => null),
      ]);
      const { ricavi, costi } = economiaSintetica({ budget: budgetSnap.empty ? null : budgetSnap.docs[0].data(),
        movimenti: movSnap.docs.map(d=>d.data()), ordini: odaSnap.docs.map(d=>d.data()), projectId: eo.projectIdCollegato,
        fontOre: orgSnap?.exists?.() ? (orgSnap.data().fontOreEconomico || "programmazione") : undefined });
      setDatiConsuntivo({ ricavi, costi, marginePerc: ricavi>0 ? ((ricavi-costi)/ricavi*100) : null });
    })();
  }, [eo?.projectIdCollegato, orgId]);


  useEffect(() => {
    if (!showCondividi || !orgId) return;
    getDocs(query(collection(db,"users"), where("organizationId","==",orgId), where("enabled","==",true)))
      .then(snap => setUtentiOrg(snap.docs.map(d=>d.data()).filter(u=>u.uid!==currentUser?.uid)));
  }, [showCondividi, orgId]);

  const condividiCon = async (utente) => {
    const giaCondivisa = eo.condivisoCon||[];
    if (giaCondivisa.includes(utente.uid)) { addToast("Già condivisa con questa persona","info"); return; }
    await updateDoc(doc(db,"elaborazioniOfferte",eo.id), { condivisoCon: [...giaCondivisa, utente.uid] });
    await addDoc(collection(db,"notifiche"), {
      userId: utente.uid, orgId, tipo:"elaborazione_offerta_condivisa",
      titolo:"Elaborazione Offerta condivisa con te",
      testo:`${currentUser?.displayName||"Un collega"} ha condiviso con te l'elaborazione ${eo.numero} (${eo.oggetto||""}) — ora puoi modificarla anche tu.`,
      letta:false, createdAt:serverTimestamp(),
    });
    addToast(`Condivisa con ${utente.displayName} ✅ — ora può modificarla anche lei/lui`,"success");
    setShowCondividi(false);
  };

  const salva = async () => {
    if (!form.oggetto.trim()) { addToast("Inserisci l'oggetto dell'offerta","error"); return; }
    if (!form.clienteId && !form.clienteNome.trim()) { addToast("Scegli un cliente o inserisci il nome di un potenziale cliente","error"); return; }
    setSaving(true);
    const dati = {
      ...form, materiali, manoOfficina, manoCantiere, speseInst,
      percUtile: percUtile||0, percSpeseGenerali: percSpeseGenerali||0, percUtileSpeseVive: percUtileSpeseVive||0,
      oreGiornoStandard: oreGiornoStandard||8,
      orgId, orgNome: orgInfo?.nome||orgInfo?.name||"",
      autonoma: !form.clienteId,
      emissione: {...emissione, descrizioneModificataInEmissione},
      descrizioneRighe: righeDescrizione,
      totaleGenerale: totali.totaleGenerale, totaleNetto: totali.totaleNetto, totaleMargini: totali.totaleMargini,
      updatedAt: serverTimestamp(),
    };
    if (eo?.id) {
      await updateDoc(doc(db,"elaborazioniOfferte",eo.id), dati);
      addToast("Elaborazione aggiornata ✅","success");
    } else {
      const anno = new Date().getFullYear();
      const prog = await prossimoProgressivo(`${orgId}_eo_${anno}`, { orgId, anno });
      const numero = `OFF-${anno}-${String(prog).padStart(4,"0")}`;
      await addDoc(collection(db,"elaborazioniOfferte"), {...dati, numero,
        compilatoreUid: currentUser?.uid, compilatoreNome: currentUser?.displayName||"",
        createdAt: serverTimestamp()});
      addToast(`Elaborazione ${numero} creata ✅`,"success");
    }
    setSaving(false);
    onClose();
  };

  const stampaPDFEmissione = () => {
    const win = window.open("","_blank");
    const coloreDoc = orgInfo?.coloreDocumenti || "#1e1b4b";
    const posizioneLogo = orgInfo?.posizioneLogo || "sinistra";
    const logoHtml = orgInfo?.logo ? `<img src="${orgInfo.logo}" style="height:46px;object-fit:contain;"/>` : `<div style="font-size:20px;font-weight:900;color:${coloreDoc};">◆ ${orgInfo?.nome||orgInfo?.name||""}</div>`;
    const blocAziendaHtml = `<div style="text-align:${posizioneLogo==="centro"?"center":posizioneLogo==="destra"?"right":"left"};">
        ${logoHtml}
        <div style="font-size:11px;color:#6b7280;margin-top:6px;line-height:1.6;">
          ${orgInfo?.indirizzo||""}${orgInfo?.piva?`<br/>P.IVA ${orgInfo.piva}`:""}
        </div>
      </div>`;
    const imponibile = totali.totaleGenerale;
    const ivaImporto = imponibile * (parseFloat(form.ivaPerc)||0)/100;
    const totaleOrdine = imponibile + ivaImporto;
    const dataOfferta = form.dataEmissione ? new Date(form.dataEmissione+"T00:00:00") : new Date();
    const validoFino = new Date(dataOfferta); validoFino.setDate(validoFino.getDate() + (parseInt(form.validitaGiorni)||30));

    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Offerta ${eo?.numero||""} — ${form.clienteNome||""}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;font-size:12.5px;color:#111;padding:16mm 18mm;}
      @media screen{body{max-width:850px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4 portrait;}
      @media print{.no-print{display:none;}}
      .hdr{display:flex;flex-direction:${posizioneLogo==="centro"?"column":"row"};justify-content:${posizioneLogo==="centro"?"center":"space-between"};align-items:${posizioneLogo==="centro"?"center":"flex-start"};border-bottom:3px solid ${coloreDoc};padding-bottom:16px;margin-bottom:20px;}
      table{width:100%;border-collapse:collapse;}
      th{background:${coloreDoc};color:#fff;padding:7px 8px;font-size:9.5px;text-align:left;text-transform:uppercase;}
      td{padding:8px;border-bottom:1px solid #e5e7eb;font-size:12px;}
      .meta td{border:none;padding:1px 6px;font-size:11.5px;}
      .footer{display:flex;justify-content:space-between;border-top:1px solid #e5e7eb;margin-top:28px;padding-top:14px;font-size:10.5px;color:#6b7280;}
    </style></head><body>
      <div class="hdr">
        <div style="order:${posizioneLogo==="destra"?2:1};">${blocAziendaHtml}</div>
        <div style="text-align:${posizioneLogo==="destra"?"left":"right"};order:${posizioneLogo==="destra"?1:2};">
          <div style="font-size:24px;font-weight:900;color:${coloreDoc};margin-bottom:8px;">Offerta</div>
          <table class="meta" style="border-spacing:6px 3px;${posizioneLogo==="destra"?"margin-left:0;":"margin-left:auto;"}">
            <tr><td style="color:#9ca3af;text-align:${posizioneLogo==="destra"?"left":"right"};">Data offerta</td><td style="font-weight:700;">${dataOfferta.toLocaleDateString("it-IT")}</td></tr>
            <tr><td style="color:#9ca3af;text-align:${posizioneLogo==="destra"?"left":"right"};">Numero offerta</td><td style="font-weight:700;color:#d97706;font-family:monospace;">${eo?.numero||""}</td></tr>
            ${emissione.numeroCliente?`<tr><td style="color:#9ca3af;text-align:${posizioneLogo==="destra"?"left":"right"};">Numero cliente</td><td style="font-weight:700;">${emissione.numeroCliente}</td></tr>`:""}
            <tr><td style="color:#9ca3af;text-align:${posizioneLogo==="destra"?"left":"right"};">Valida fino al</td><td style="font-weight:700;">${validoFino.toLocaleDateString("it-IT")}</td></tr>
            <tr><td style="color:#9ca3af;text-align:${posizioneLogo==="destra"?"left":"right"};">Redatta da</td><td style="font-weight:700;">${emissione.redattaDa||""}</td></tr>
            ${form.referente?`<tr><td style="color:#9ca3af;text-align:${posizioneLogo==="destra"?"left":"right"};">Referente</td><td style="font-weight:700;">${form.referente}</td></tr>`:""}
          </table>
        </div>
      </div>

      <div style="margin-bottom:16px;">
        <div style="font-size:13px;font-weight:700;color:#111827;">${form.clienteNome||""}</div>
        ${clienteSelezionato?.indirizzo?`<div style="font-size:11px;color:#6b7280;margin-top:2px;">${clienteSelezionato.indirizzo}</div>`:""}
        ${form.sitoCliente?`<div style="font-size:11px;color:#7c3aed;font-weight:600;margin-top:2px;">📍 Sito: ${form.sitoCliente}</div>`:""}
      </div>

      <div style="margin-bottom:6px;">Spett.le ${form.referente||form.clienteNome||"Cliente"},</div>
      <div style="font-size:12.5px;line-height:1.6;margin-bottom:18px;">${(emissione.testoIntroduttivo||"").replace(/\n/g,"<br/>")}</div>

      <table style="margin-bottom:4px;">
        <thead><tr><th>Pos</th><th>Descrizione</th><th style="text-align:right;">Totale</th></tr></thead>
        <tbody><tr>
          <td>10</td>
          <td>${(emissione.descrizioneOfferta||"").replace(/\n/g,"<br/>")}</td>
          <td style="font-weight:800;color:#059669;text-align:right;">€ ${fmt(imponibile)}</td>
        </tr></tbody>
      </table>

      <div style="font-size:11.5px;margin-top:16px;line-height:1.8;">
        ${form.condizioniPagamento?`<div><strong>Condizioni di pagamento:</strong> ${form.condizioniPagamento}</div>`:""}
        ${emissione.condizioniFornitura?`<div><strong>Condizioni di fornitura:</strong> ${emissione.condizioniFornitura}</div>`:""}
        ${emissione.esclusioni?`<div><strong>Esclusioni:</strong> ${emissione.esclusioni}</div>`:""}
      </div>

      <div style="margin-left:auto;width:280px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:10px;padding:12px 16px;margin-top:18px;">
        <div style="display:flex;justify-content:space-between;font-size:12px;margin-bottom:4px;"><span>Totale imponibile</span><strong>€ ${fmt(imponibile)}</strong></div>
        <div style="display:flex;justify-content:space-between;font-size:12px;margin-bottom:8px;"><span>IVA</span><strong>€ ${fmt(ivaImporto)}</strong></div>
        <div style="display:flex;justify-content:space-between;font-size:14px;font-weight:800;color:#059669;border-top:2px solid ${coloreDoc};padding-top:8px;"><span>Totale ordine</span><span>€ ${fmt(totaleOrdine)}</span></div>
      </div>

      <div style="font-size:12px;margin-top:26px;">Per eventuali domande rimaniamo a Sua completa disposizione.</div>
      <div style="font-size:12px;margin-top:16px;">Cordiali Saluti,</div>
      <div style="display:flex;justify-content:space-between;margin-top:40px;font-size:11px;color:#6b7280;">
        <div>Data: _______________________</div>
        <div>Timbro e firma per accettazione: _______________________</div>
      </div>

      <div class="footer">
        <div><strong style="color:#111827;">${orgInfo?.nome||orgInfo?.name||""}</strong><br/>${orgInfo?.indirizzo||""}</div>
        <div style="text-align:right;">${emissione.redattaDa||""}${orgInfo?.telefono?`<br/>Tel. ${orgInfo.telefono}`:""}${orgInfo?.email?`<br/>${orgInfo.email}`:""}</div>
      </div>
      ${orgInfo?.piePagina?`<div style="margin-top:10px;padding-top:10px;border-top:1px solid #f3f4f6;font-size:9.5px;color:#9ca3af;text-align:center;white-space:pre-line;">${orgInfo.piePagina}</div>`:""}
    </body></html>`);
    win.document.close();
    setTimeout(() => {
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print";
      btn.style.cssText = "position:fixed;top:16px;right:16px;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(124,58,237,.3);z-index:9999;";
      btn.onclick = () => win.print();
      win.document.body.appendChild(btn);
    }, 50);
  };

  const stampaPDF = () => {
    const win = window.open("","_blank");
    const logoHtml = orgInfo?.logo ? `<img src="${orgInfo.logo}" style="height:40px;object-fit:contain;"/>` : `<div style="font-size:18px;font-weight:900;color:#1e1b4b;">◆ ${orgInfo?.nome||orgInfo?.name||""}</div>`;
    const rigaTab = (titolo, righe, cols, allineamenti) => righe.length===0 ? "" : `
      <div style="margin-top:14px;">
        <div style="background:#1e1b4b;color:#fff;padding:7px 12px;font-size:12px;font-weight:700;border-radius:8px 8px 0 0;">${titolo}</div>
        <table style="width:100%;border-collapse:collapse;border:1px solid #e5e7eb;border-top:none;">
          <thead><tr style="background:#f9fafb;">${cols.map((c,i)=>`<th style="padding:5px 8px;font-size:9px;color:#6b7280;text-align:${allineamenti?.[i]||"left"};text-transform:uppercase;">${c}</th>`).join("")}</tr></thead>
          <tbody>${righe}</tbody>
        </table>
      </div>`;
    const materialiRows = materiali.filter(m=>m.descrizione).map(m=>{
      const scontato=(parseFloat(m.prezzoListino)||0)*(1-(parseFloat(m.scontoPerc)||0)/100);
      return `<tr><td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #f3f4f6;">${m.quantita||"—"}</td><td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #f3f4f6;">${m.descrizione}</td><td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #f3f4f6;text-align:right;">€${fmt(scontato*(parseFloat(m.quantita)||0))}</td></tr>`;
    }).join("");
    const manoRows = (arr) => arr.filter(r=>r.descrizione).map(r=>{
      const ore=(parseFloat(r.persone)||0)*(parseFloat(r.giorni)||0)*8;
      return `<tr><td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #f3f4f6;">${r.descrizione}</td><td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #f3f4f6;text-align:right;">${ore}h</td><td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #f3f4f6;text-align:right;">€${fmt(ore*(parseFloat(r.prezzoOrario)||0))}</td></tr>`;
    }).join("");
    const speseRows = speseInst.filter(r=>r.descrizione).map(r=>
      `<tr><td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #f3f4f6;">${r.descrizione}</td><td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #f3f4f6;text-align:right;">€${fmt((parseFloat(r.quantita)||0)*(parseFloat(r.prezzoUnitario)||0))}</td></tr>`
    ).join("");

    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>${form.oggetto||"Elaborazione Offerta"}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;font-size:12px;color:#111;padding:15mm 18mm;}
      @media screen{body{max-width:850px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4 portrait;}
      @media print{.no-print{display:none;}}
      .hdr{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #1e1b4b;padding-bottom:12px;margin-bottom:16px;}
    </style></head><body>
      <div class="hdr">
        <div>${logoHtml}<div style="font-size:10px;color:#6b7280;margin-top:3px;">${orgInfo?.nome||orgInfo?.name||""}</div></div>
        <div style="text-align:right;">
          <div style="font-size:13px;font-weight:800;color:#d97706;font-family:monospace;">${eo?.numero||"BOZZA"}</div>
          <div style="font-size:15px;font-weight:800;color:#1e1b4b;margin-top:2px;">📐 ${form.oggetto||"Elaborazione Offerta"}</div>
          <div style="font-size:10px;color:#6b7280;margin-top:2px;">${form.clienteNome||""} — ${form.dataEmissione?form.dataEmissione.split("-").reverse().join("/"):""}</div>
        </div>
      </div>
      ${rigaTab("📦 Materiali", materialiRows, ["Q.tà","Descrizione","Totale"], ["left","left","right"])}
      ${rigaTab("🔧 Manodopera Officina", manoRows(manoOfficina), ["Descrizione","Ore","Totale"], ["left","right","right"])}
      ${rigaTab("👷 Manodopera Cantiere", manoRows(manoCantiere), ["Descrizione","Ore","Totale"], ["left","right","right"])}
      ${rigaTab("🚚 Spese di Installazione", speseRows, ["Descrizione","Totale"], ["left","right"])}

      <div style="margin-top:18px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:10px;padding:14px 18px;">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px 20px;font-size:12px;">
          <div>Materiali</div><div style="text-align:right;font-weight:700;">€${fmt(totali.totaleMateriali)}</div>
          <div>Manodopera</div><div style="text-align:right;font-weight:700;">€${fmt(totali.totaleManodopera)}</div>
          <div>Spese di Installazione</div><div style="text-align:right;font-weight:700;">€${fmt(totali.totaleSpeseInst)}</div>
          <div style="border-top:1px solid #e5e7eb;padding-top:6px;font-weight:700;">Totale Netto</div><div style="border-top:1px solid #e5e7eb;padding-top:6px;text-align:right;font-weight:800;">€${fmt(totali.totaleNetto)}</div>
          <div>Utile (${percUtile||0}%)</div><div style="text-align:right;font-weight:700;">€${fmt(totali.totaleUtile)}</div>
          <div>Spese Generali (${percSpeseGenerali||0}%)</div><div style="text-align:right;font-weight:700;">€${fmt(totali.totaleSpeseGenerali)}</div>
          <div>Utile su Spese Vive (${percUtileSpeseVive||0}%)</div><div style="text-align:right;font-weight:700;">€${fmt(totali.totaleUtileSpeseVive)}</div>
        </div>
        <div style="display:flex;justify-content:space-between;align-items:center;margin-top:12px;padding-top:12px;border-top:2px solid #1e1b4b;">
          <div style="font-size:13px;font-weight:800;color:#1e1b4b;text-transform:uppercase;">Totale Generale Offerta</div>
          <div style="font-size:24px;font-weight:900;color:#059669;">€${fmt(totali.totaleGenerale)}</div>
        </div>
        ${totali.prezzoUnitario!==null?`<div style="text-align:right;font-size:11px;color:#6b7280;margin-top:4px;">Prezzo unitario (${form.quantitaLotti} pz/lotti): €${fmt(totali.prezzoUnitario)}</div>`:""}
      </div>
      ${form.note?`<div style="margin-top:14px;font-size:11px;color:#6b7280;"><strong>Note:</strong> ${form.note}</div>`:""}
    </body></html>`);
    win.document.close();
    setTimeout(() => {
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print";
      btn.style.cssText = "position:fixed;top:16px;right:16px;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(124,58,237,.3);z-index:9999;";
      btn.onclick = () => win.print();
      win.document.body.appendChild(btn);
    }, 50);
  };

  if (confronto) return <VistaConfronto dati={confronto} onTorna={()=>setConfronto(null)}/>;

  const clienteScelto = !!(form.clienteId || form.clienteNome?.trim());
  const inpTop = {padding:"6px 10px",border:"1px solid rgba(255,255,255,.2)",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",background:"#fff"};

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      <div style={{background:"#1e1b4b",padding:"12px 20px"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
          <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:"#c4b5fd",display:"flex",alignItems:"center",gap:4,fontSize:13,fontWeight:600}}>
            <ChevronLeft size={15}/> {eo?"Indietro":"Annulla"}
          </button>
          {eo?.numero && <span style={{fontFamily:"monospace",fontWeight:800,color:"#fbbf24",fontSize:14}}>{eo.numero}</span>}
          <input disabled={soloLetturaEffettivo} style={{...inpTop,flex:1}} value={form.oggetto||""} onChange={e=>upd("oggetto",e.target.value)} placeholder="Oggetto dell'offerta *"/>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          {/* Selettore cliente: esistente da menu a tendina, oppure nuovo/generico a testo libero — larghezza fissa, non invade il resto della riga */}
          {!nuovoClienteGenerico ? (
            <select disabled={soloLetturaEffettivo} style={{...inpTop,width:220,flexShrink:0}} value={form.clienteId||""}
              onChange={e=>{
                const c = clienti.find(x=>x.id===e.target.value);
                setForm(f=>({...f, clienteId:e.target.value, clienteNome:c?.ragioneSociale||"",
                  condizioniPagamento: f.condizioniPagamento || c?.condizioniPagamentoAbituali || ""}));
                updEm("numeroCliente", c?.numeroCliente||"");
              }}>
              <option value="">— Seleziona cliente * —</option>
              {clienti.map(c=><option key={c.id} value={c.id}>{c.ragioneSociale}</option>)}
            </select>
          ) : (
            <input disabled={soloLetturaEffettivo} style={{...inpTop,width:220,flexShrink:0}} value={form.clienteNome||""}
              onChange={e=>setForm(f=>({...f, clienteId:"", clienteNome:e.target.value}))}
              placeholder="Nome nuovo cliente / potenziale *"/>
          )}
          {nuovoClienteGenerico && !soloLetturaEffettivo && form.clienteNome?.trim() && (
            <button type="button" onClick={creaClienteRapido} disabled={creandoCliente}
              title="Crea subito questo cliente in Anagrafica, così riceve il suo numero"
              style={{fontSize:11,fontWeight:700,color:"#fff",background:"#7c3aed",border:"none",borderRadius:7,padding:"6px 10px",cursor:"pointer",whiteSpace:"nowrap"}}>
              {creandoCliente?"Creo...":"👤+ Crea in anagrafica"}
            </button>
          )}
          {!soloLetturaEffettivo && (
            <button type="button" onClick={()=>{ setNuovoClienteGenerico(v=>!v); setForm(f=>({...f, clienteId:"", clienteNome:""})); updEm("numeroCliente",""); }}
              title={nuovoClienteGenerico?"Scegli da elenco clienti":"Cliente nuovo, non ancora in anagrafica"}
              style={{fontSize:11,color:"#c4b5fd",background:"none",border:"none",cursor:"pointer",fontWeight:600,whiteSpace:"nowrap"}}>
              {nuovoClienteGenerico?"↩ elenco clienti":"+ nuovo cliente"}
            </button>
          )}

          <input disabled={soloLetturaEffettivo} type="date" style={{...inpTop,flexShrink:0}} value={form.dataEmissione||""} onChange={e=>upd("dataEmissione",e.target.value)}/>
          <select disabled={finalizzataBloccata||soloLetturaEffettivo} style={{...inpTop,flexShrink:0}} value={form.stato||"bozza"} onChange={e=>upd("stato",e.target.value)}>
            {Object.entries(STATI_EO).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
          </select>
          {!finalizzataBloccata && !nonModificabilePerAltri && (
            <span style={{fontSize:10.5,color:"#c4b5fd",fontStyle:"italic",maxWidth:200}}>
              ⚠️ Una volta impostata su "Finalizzata" e salvata, non sarà più modificabile da nessuno.
            </span>
          )}
          {finalizzataBloccata && (
            <span style={{fontSize:10.5,color:"#c4b5fd",fontStyle:"italic"}}>
              🔒 Finalizzata — non più modificabile.
            </span>
          )}
          <div style={{flex:1}}/>
          <button onClick={vista==="emissione"?stampaPDFEmissione:stampaPDF} style={{display:"flex",alignItems:"center",gap:5,padding:"6px 12px",border:"1px solid #a78bfa",borderRadius:7,background:"rgba(255,255,255,.08)",color:"#e9d5ff",fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>
            🖨️ Stampa PDF
          </button>
          {eo && eo.compilatoreUid===currentUser?.uid && (
            <button onClick={()=>setShowCondividi(true)} style={{display:"flex",alignItems:"center",gap:5,padding:"6px 12px",border:"1px solid #a78bfa",borderRadius:7,background:"rgba(255,255,255,.08)",color:"#e9d5ff",fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>
              🔗 Condividi{(eo.condivisoCon||[]).length>0 && ` (${eo.condivisoCon.length})`}
            </button>
          )}
          {!soloLetturaEffettivo && (
          <button onClick={salva} disabled={saving} className="btn btn-primary" style={{padding:"6px 16px",fontSize:12}}>
            {saving?"Salvataggio...":"💾 Salva"}
          </button>
          )}
        </div>
      </div>

      {showCondividi && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={()=>setShowCondividi(false)}>
          <div style={{background:"#fff",borderRadius:14,padding:20,width:340,maxHeight:"70vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <h3 style={{fontSize:14,fontWeight:700,margin:0}}>🔗 Condividi elaborazione</h3>
              <button onClick={()=>setShowCondividi(false)} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af"}}><X size={16}/></button>
            </div>
            <p style={{fontSize:11.5,color:"#6b7280",marginBottom:14}}>Da quel momento la persona scelta potrà modificare questa elaborazione insieme a te — riceverà anche un avviso in Nexiora.</p>
            {utentiOrg.length===0 ? (
              <div style={{fontSize:12,color:"#9ca3af",textAlign:"center",padding:20}}>Nessun altro utente trovato nella tua organizzazione</div>
            ) : utentiOrg.map(u => {
              const giaCondivisa = (eo.condivisoCon||[]).includes(u.uid);
              return (
                <button key={u.uid} onClick={()=>condividiCon(u)} disabled={giaCondivisa}
                  style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",padding:"9px 10px",border:"1px solid #f3f4f6",borderRadius:8,marginBottom:6,background:giaCondivisa?"#f0fdf4":"#fff",cursor:giaCondivisa?"default":"pointer",textAlign:"left"}}>
                  <span style={{fontSize:12.5,fontWeight:600,color:"#111827"}}>{u.displayName}</span>
                  {giaCondivisa && <span style={{fontSize:10,color:"#059669",fontWeight:700}}>✓ già condivisa</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Le due sezioni collegate: Elaborazione (calcolo interno) ed Emissione (documento per il cliente) */}
      <div style={{display:"flex",gap:0,borderBottom:"1px solid #e5e7eb",padding:"0 20px",flexShrink:0,background:"#fafafa"}}>
        {[{id:"elaborazione",label:"📊 Elaborazione",desc:"calcolo interno"},{id:"emissione",label:"📄 Emissione Offerta",desc:"documento per il cliente"}].map(v=>(
          <button key={v.id} onClick={()=>setVista(v.id)}
            style={{padding:"10px 18px",border:"none",borderBottom:vista===v.id?"3px solid #7c3aed":"3px solid transparent",
              background:"none",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            <div style={{fontSize:13,fontWeight:vista===v.id?800:600,color:vista===v.id?"#7c3aed":"#6b7280"}}>{v.label}</div>
            <div style={{fontSize:10,color:"#9ca3af"}}>{v.desc}</div>
          </button>
        ))}
      </div>

      {soloLetturaEffettivo && (
        <div style={{padding:"6px 20px",background: finalizzataBloccata?"#f5f3ff":nonModificabilePerAltri?"#fef3c7":"#f9fafb",borderBottom:"1px solid #e5e7eb",fontSize:11,color: finalizzataBloccata?"#5b21b6":nonModificabilePerAltri?"#92400e":"#6b7280"}}>
          {finalizzataBloccata
            ? `🔒 Offerta Finalizzata — non è più modificabile da nessuno, compreso chi l'ha creata. Puoi solo consultarla o stamparla.`
            : nonModificabilePerAltri
            ? `🔒 Modificabile solo da chi l'ha creata (${eo.compilatoreNome||"?"})${(eo.condivisoCon||[]).length>0?" o da chi ne ha ricevuto la condivisione":""}. Puoi solo consultarla.`
            : `👁️ Sola consultazione — per modificarla vai dalla commessa di origine, o da Personale per quelle senza commessa`}
        </div>
      )}

      <fieldset disabled={!!soloLetturaEffettivo} style={{border:"none",padding:0,margin:0,display:"flex",flexDirection:"column",flex:1,overflow:"hidden",minHeight:0}}>

      {vista==="elaborazione" ? (
      <div style={{flex:1,display:"flex",overflow:"hidden"}}>
        <div style={{flex:1,overflowY:"auto",padding:"16px 20px",minWidth:0}}>
          <div style={{background:"#f3f4f6",border:"1px solid #e5e7eb",borderRadius:10,padding:"9px 14px",marginBottom:14,display:"flex",alignItems:"center",gap:9}}>
            <span style={{fontSize:15}}>🔒</span>
            <span style={{fontSize:11.5,color:"#4b5563"}}>Questa scheda è a uso esclusivamente interno — contiene i costi e i margini dell'azienda. Al cliente va inoltrata solo la scheda <strong>"📄 Emissione Offerta"</strong>, che non mostra mai questi dati.</span>
          </div>

          <AvvisoSimili dati={avviso} onVaiAlConfronto={setConfronto}/>

          {!clienteScelto && (
            <div style={{background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:10,padding:"10px 14px",marginBottom:14,display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:16}}>ℹ️</span>
              <span style={{fontSize:12,color:"#1e40af"}}>Scegli un cliente dall'elenco (o clicca "+ nuovo cliente" qui sopra) prima di iniziare a compilare i costi — l'elaborazione va sempre riferita a un cliente.</span>
            </div>
          )}

          <fieldset disabled={!clienteScelto} style={{border:"none",padding:0,margin:0,opacity:clienteScelto?1:.55}}>

          {listinoMateriali.length>0 && (
            <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:8,padding:"7px 12px",marginBottom:10,fontSize:11,color:"#5b21b6"}}>
              💜 Nei materiali puoi cercare 🔍 dal Listino Materiali per compilare descrizione, prezzo e sconto in automatico — oppure scrivere liberamente come sempre. Se non prendi dal listino, il prezzo resta interamente sotto il tuo controllo, senza nessun confronto automatico con quanto praticato di solito.
            </div>
          )}

          <BloccoTabella titolo="📦 Materiali" colore="#1e1b4b"
            colonne={["Q.tà","Descrizione","Codice","Listino","Sconto %","Scontato","Totale",""]}
            righe={materiali} setRighe={setMateriali} RigaComp={RigaMateriale}
            extraProps={{listino:listinoMateriali}}
            nuovaRiga={()=>({descrizione:"",codice:"",quantita:"",prezzoListino:"",scontoPerc:""})}/>

          <BloccoTabella titolo="🔧 Manodopera Officina" colore="#7c3aed"
            colonne={["Descrizione","Persone","Giorni","Ore","€/ora","Totale",""]}
            righe={manoOfficina} setRighe={setManoOfficina} RigaComp={RigaManodopera} onVerifica={verificaSimili}
            nuovaRiga={()=>({descrizione:"",persone:"",giorni:"",prezzoOrario:orgInfo?.costoOrarioBase||""})}/>

          <BloccoTabella titolo="👷 Manodopera Cantiere" colore="#0369a1"
            colonne={["Descrizione","Persone","Giorni","Ore","€/ora","Totale",""]}
            righe={manoCantiere} setRighe={setManoCantiere} RigaComp={RigaManodopera} onVerifica={verificaSimili}
            nuovaRiga={()=>({descrizione:"",persone:"",giorni:"",prezzoOrario:orgInfo?.costoOrarioBase||""})}/>

          <BloccoTabella titolo="🚚 Spese di Installazione" colore="#166534"
            colonne={["Descrizione","Q.tà/gg","Prezzo un.","Totale",""]}
            righe={speseInst} setRighe={setSpeseInst} RigaComp={RigaSpesaInstallazione}
            nuovaRiga={()=>({descrizione:"",quantita:"",prezzoUnitario:""})}/>
          </fieldset>

          {/* Referente, Condizioni di pagamento, IVA — costruite qui, l'Emissione le riceve già pronte, sola lettura */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr auto auto",gap:12,marginBottom:14}}>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>👤 Referente cliente <span style={{fontWeight:400,color:"#9ca3af"}}>— passa in automatico all'Emissione</span></label>
              <input value={form.referente||""} onChange={e=>upd("referente",e.target.value)} placeholder="Es. Ing. Mario Rossi"
                style={{width:"100%",padding:"6px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",color:"#111827",boxSizing:"border-box"}}/>
            </div>
            <div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                <label style={{fontSize:11,fontWeight:600,color:"#6b7280"}}>💳 Condizioni di pagamento</label>
                <SelettoreModello modelli={MODELLI_PAGAMENTO} onScegli={v=>upd("condizioniPagamento",v)}/>
              </div>
              <input value={form.condizioniPagamento||""} onChange={e=>upd("condizioniPagamento",e.target.value)} placeholder="Scegli un modello o scrivi liberamente"
                style={{width:"100%",padding:"6px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",color:"#111827",boxSizing:"border-box"}}/>
              {clienteSelezionato?.condizioniPagamentoAbituali && form.condizioniPagamento && form.condizioniPagamento!==clienteSelezionato.condizioniPagamentoAbituali && (
                <div style={{fontSize:10.5,color:"#92400e",background:"#fffbeb",border:"1px solid #fde68a",borderRadius:6,padding:"5px 8px",marginTop:5}}>
                  ⚠️ Per questo cliente di solito si usa <strong>"{clienteSelezionato.condizioniPagamentoAbituali}"</strong> — qui è diverso, verifica che sia voluto.
                </div>
              )}
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>IVA</label>
              <select value={form.ivaPerc??22} onChange={e=>upd("ivaPerc",e.target.value)}
                style={{padding:"6px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",color:"#111827"}}>
                <option value={0}>0%</option><option value={7}>7%</option><option value={22}>22%</option>
              </select>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Validità offerta</label>
              <div style={{display:"flex",gap:4}}>
                {[30,45,60].map(g=>(
                  <button key={g} type="button" onClick={()=>upd("validitaGiorni",g)}
                    style={{fontSize:11,fontWeight:700,padding:"6px 9px",borderRadius:6,cursor:"pointer",
                      border:`1px solid ${(parseInt(form.validitaGiorni)||30)===g?"#7c3aed":"#e5e7eb"}`,
                      background:(parseInt(form.validitaGiorni)||30)===g?"#7c3aed":"#fff",
                      color:(parseInt(form.validitaGiorni)||30)===g?"#fff":"#6b7280"}}>
                    {g}gg
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Sito cliente — utile quando il cliente ha più sedi o stabilimenti, diverso dall'indirizzo legale */}
          <div style={{marginBottom:14}}>
            <SelettoreSede clienteId={form.clienteId||""} etichetta="📍 Sede / sito dei lavori (opzionale)"
              value={form.sede || (form.sitoCliente ? copiaLibera(form.sitoCliente) : null)}
              onChange={c=>setForm(f=>({...f, sede:c||null, sitoCliente:descrizioneSede(c)}))}/>
            <div style={{fontSize:10.5,color:"#9ca3af",marginTop:3}}>Dalle sedi registrate nell'anagrafica del cliente — passa alla commessa quando la crei.</div>
          </div>

          {/* Descrizione offerta a righe — ogni riga si aggiunge con un clic, tutte insieme
              formano la descrizione che passa in automatico all'Emissione */}
          <div style={{marginBottom:14}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
              <label style={{fontSize:11,fontWeight:600,color:"#6b7280"}}>📝 Descrizione offerta <span style={{fontWeight:400,color:"#9ca3af"}}>— passa in automatico all'Emissione, resta comunque modificabile lì</span></label>
              <button type="button" onClick={()=>{
                  const generate = generaRigheDescrizione(materiali, manoOfficina, manoCantiere, speseInst);
                  if (generate.length===0) { addToast("Inserisci prima almeno una lavorazione con descrizione","info"); return; }
                  if (righeDescrizione.some(r=>r.trim()) && !window.confirm("Ci sono già delle righe scritte — sostituirle con quelle generate dalle lavorazioni?")) return;
                  setRigheDescrizione(generate);
                }}
                style={{fontSize:10.5,fontWeight:700,color:"#7c3aed",background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:6,padding:"3px 9px",cursor:"pointer",whiteSpace:"nowrap"}}>
                ✨ Genera dalle lavorazioni
              </button>
            </div>
            {righeDescrizione.map((riga,i)=>(
              <div key={i} style={{display:"flex",gap:6,marginBottom:6}}>
                <input value={riga} onChange={e=>{
                    const nuove=[...righeDescrizione]; nuove[i]=e.target.value; setRigheDescrizione(nuove);
                  }}
                  placeholder={`Riga ${i+1} — es. Fornitura e posa in opera di impianto elettrico completo...`}
                  style={{flex:1,padding:"6px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",color:"#111827"}}/>
                <button type="button" onClick={()=>setRigheDescrizione(righeDescrizione.filter((_,j)=>j!==i))}
                  style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5",padding:"0 4px"}}>
                  <Trash2 size={13}/>
                </button>
              </div>
            ))}
            <button type="button" onClick={()=>setRigheDescrizione([...righeDescrizione,""])}
              style={{display:"flex",alignItems:"center",gap:5,padding:"6px 10px",border:"1px dashed #e5e7eb",borderRadius:8,background:"#fafafa",color:"#7c3aed",fontSize:11.5,fontWeight:700,cursor:"pointer",width:"100%",justifyContent:"center"}}>
              <Plus size={12}/> Aggiungi riga
            </button>
          </div>

          {/* Margini */}
          <div style={{border:"1px solid #e5e7eb",borderRadius:10,padding:"14px 16px",marginBottom:14,background:"#fffbeb"}}>
            <div style={{fontSize:12.5,fontWeight:700,color:"#92400e",marginBottom:10}}>💰 Margini (modificabili per questa elaborazione)</div>

            {storicoMargineCliente && storicoMargineCliente.media>1 && (
              <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:8,padding:"9px 12px",marginBottom:12,fontSize:11.5,color:"#5b21b6",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <span style={{flex:1}}>
                  💜 Con questo cliente, sulle ultime {storicoMargineCliente.n} offerte concluse, il margine reale è finito in media <strong>{storicoMargineCliente.media.toFixed(1)} punti</strong> sotto quello scritto qui. Vuoi alzare l'Utile di sicurezza?
                </span>
                <button type="button" onClick={()=>setPercUtile(v=>String((parseFloat(v)||0)+Math.round(storicoMargineCliente.media)))}
                  style={{padding:"5px 12px",borderRadius:6,border:"none",background:"#7c3aed",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",flexShrink:0}}>
                  +{Math.round(storicoMargineCliente.media)}% Utile
                </button>
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12}}>
              <div>
                <label style={{fontSize:10,fontWeight:600,color:"#92400e",display:"block",marginBottom:3}}>Utile % (su Materiali+Manodopera)</label>
                <input className="eo-riga-costo" style={{...num,fontSize:22,fontWeight:800,color:"#111827",padding:"8px 6px"}} type="number" step="0.1" min="0" value={percUtile??""} onChange={e=>setPercUtile(nonNegativo(e.target.value))}/>
              </div>
              <div>
                <label style={{fontSize:10,fontWeight:600,color:"#92400e",display:"block",marginBottom:3}}>Spese Generali % (su Materiali+Manodopera)</label>
                <input className="eo-riga-costo" style={{...num,fontSize:22,fontWeight:800,color:"#111827",padding:"8px 6px"}} type="number" step="0.1" min="0" value={percSpeseGenerali??""} onChange={e=>setPercSpeseGenerali(nonNegativo(e.target.value))}/>
              </div>
              <div>
                <label style={{fontSize:10,fontWeight:600,color:"#92400e",display:"block",marginBottom:3}}>Utile su Spese Vive % (su Spese Installazione)</label>
                <input className="eo-riga-costo" style={{...num,fontSize:22,fontWeight:800,color:"#111827",padding:"8px 6px"}} type="number" step="0.1" min="0" value={percUtileSpeseVive??""} onChange={e=>setPercUtileSpeseVive(nonNegativo(e.target.value))}/>
              </div>
              <div>
                <label style={{fontSize:10,fontWeight:600,color:"#92400e",display:"block",marginBottom:3}}>Quantità/lotti (per prezzo unitario)</label>
                <input className="eo-riga-costo" style={num} type="number" min="0" value={form.quantitaLotti||""} onChange={e=>upd("quantitaLotti",nonNegativo(e.target.value))} placeholder="Es. 60"/>
              </div>
            </div>
          </div>

          <div>
            <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Note</label>
            <textarea value={form.note||""} onChange={e=>upd("note",e.target.value)} rows={2}
              style={{width:"100%",padding:"6px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",color:"#111827",resize:"vertical"}}/>
          </div>
        </div>

        <RiepilogoEconomicoPanel totali={totali} percUtile={percUtile} percSpeseGenerali={percSpeseGenerali} percUtileSpeseVive={percUtileSpeseVive}
          commessaSuggerita={commessaSuggerita} onCollegaCommessa={collegaCommessa}
          projectNomeCollegato={eo?.projectNomeCollegato} datiConsuntivo={datiConsuntivo}
          finalizzataBloccata={finalizzataBloccata} onCreaCommessa={creaCommessaDaOfferta} creandoCliente={creandoCliente}/>
      </div>
      ) : (
        <VistaEmissione
          form={form} emissione={emissione} updEm={updEm} totali={totali}
          orgInfo={orgInfo} numero={eo?.numero} clienteSelezionato={clienteSelezionato}
          onDescrizioneModificataAMano={()=>setDescrizioneModificataInEmissione(true)}/>
      )}
      </fieldset>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════════════════
export default function ElaborazioneOffertaTab({ deepLink, onNavigate }) {
  const { currentUser } = useAuth();
  const { effectiveOrgId: orgId } = useOrg();
  const { addToast } = useToast();
  const [elaborazioni, setElaborazioni] = useState([]);
  const [orgInfo, setOrgInfo] = useState(null);
  const [clienti, setClienti] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editEO, setEditEO] = useState(null);
  const [cerca, setCerca] = useState("");
  // "inCorso" = bozze ed emesse insieme (da Nexiora Insight → Sezione Commerciale, «Offerte in corso»)
  const [filtroStato, setFiltroStato] = useState(deepLink?.filtroStato || "");
  const [soloMie, setSoloMie] = useState(false);
  const [filtroClienteId, setFiltroClienteId] = useState(deepLink?.clienteId || null);
  const [filtroClienteNome, setFiltroClienteNome] = useState(deepLink?.clienteNome || "");
  const [filtroMese, setFiltroMese] = useState("");
  const [filtroAnno, setFiltroAnno] = useState("");

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(query(collection(db,"clienti"), where("organizationId","==",orgId)),
      snap => setClienti(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.ragioneSociale||"").localeCompare(b.ragioneSociale||"","it"))));
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(query(collection(db,"elaborazioniOfferte"),where("orgId","==",orgId)),
      snap=>setElaborazioni(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0))));
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    getDoc(doc(db,"organizations",orgId)).then(snap=>{if(snap.exists())setOrgInfo(snap.data());});
  }, [orgId]);

  const elimina = async id => {
    if (!await confirm("Eliminare questa elaborazione?",{type:"danger"})) return;
    await deleteDoc(doc(db,"elaborazioniOfferte",id));
    addToast("Eliminata","info");
  };

  const filtrate = elaborazioni.filter(e => {
    if (filtroClienteId && e.clienteId!==filtroClienteId) return false;
    if (soloMie && e.compilatoreUid!==currentUser?.uid) return false;
    if (filtroStato === "inCorso" ? !(e.stato === "bozza" || e.stato === "emessa") : (filtroStato && e.stato!==filtroStato)) return false;
    if (filtroMese && e.dataEmissione?.split("-")[1]!==filtroMese) return false;
    if (filtroAnno && e.dataEmissione?.split("-")[0]!==filtroAnno) return false;
    if (cerca) {
      const s = cerca.toLowerCase();
      return (e.numero||"").toLowerCase().includes(s) || (e.oggetto||"").toLowerCase().includes(s) || (e.clienteNome||"").toLowerCase().includes(s);
    }
    return true;
  });

  const anniDisponibili = [...new Set(elaborazioni.map(e=>e.dataEmissione?.split("-")[0]).filter(Boolean))].sort((a,b)=>b-a);
  const MESI_FILTRO = ["01-Gen","02-Feb","03-Mar","04-Apr","05-Mag","06-Giu","07-Lug","08-Ago","09-Set","10-Ott","11-Nov","12-Dic"];

  if (showForm||editEO) {
    return <EOForm key={editEO?.id || "nuova"} eo={editEO} orgId={orgId} orgInfo={orgInfo} onNavigate={onNavigate}
      onClose={()=>{setShowForm(false);setEditEO(null);}}/>;
  }

  const inp = {padding:"6px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none"};

  return (
    <div style={{padding:20, height:"100%", overflowY:"auto"}}>
      <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:6,flexWrap:"wrap"}}>
        <h2 style={{fontSize:18,fontWeight:800,color:"#1e1b4b",margin:0,display:"flex",alignItems:"center",gap:8}}>📐 Elaborazione e Emissione Offerte</h2>
        <button onClick={()=>setShowForm(true)} className="btn btn-primary"
          style={{display:"flex",alignItems:"center",gap:5,padding:"6px 14px",fontSize:12}}>
          <Plus size={13}/> Nuova elaborazione
        </button>
      </div>
      <p style={{fontSize:12,color:"#9ca3af",marginBottom:16}}>{filtrate.length} elaborazione/i · Un unico registro per tutta l'organizzazione, indipendente dalle commesse</p>

      <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",marginBottom:16}}>
        <div style={{position:"relative"}}>
          <Search size={13} style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)",color:"#9ca3af"}}/>
          <input value={cerca} onChange={e=>setCerca(e.target.value)} placeholder="Cerca numero, oggetto, cliente..."
            style={{...inp,paddingLeft:26,width:240}}/>
        </div>
        <button onClick={()=>setSoloMie(v=>!v)}
          style={{padding:"6px 12px",border:"1px solid "+(soloMie?"#7c3aed":"#e5e7eb"),borderRadius:8,
            background:soloMie?"#f5f3ff":"#fff",color:soloMie?"#5b21b6":"#374151",fontSize:12,fontWeight:600,cursor:"pointer"}}>
          ✍️ Create da me
        </button>
        <select value={filtroClienteId||""} onChange={e=>{
            const c = clienti.find(x=>x.id===e.target.value);
            setFiltroClienteId(e.target.value||null); setFiltroClienteNome(c?.ragioneSociale||"");
          }} style={inp}>
          <option value="">Tutti i clienti</option>
          {clienti.map(c=><option key={c.id} value={c.id}>{c.ragioneSociale}</option>)}
        </select>
        <select value={filtroMese} onChange={e=>setFiltroMese(e.target.value)} style={inp}>
          <option value="">Tutti i mesi</option>
          {MESI_FILTRO.map(m=><option key={m} value={m.slice(0,2)}>{m.slice(3)}</option>)}
        </select>
        <select value={filtroAnno} onChange={e=>setFiltroAnno(e.target.value)} style={inp}>
          <option value="">Tutti gli anni</option>
          {anniDisponibili.map(a=><option key={a} value={a}>{a}</option>)}
        </select>
        <select value={filtroStato} onChange={e=>setFiltroStato(e.target.value)} style={inp}>
          <option value="">Tutti gli stati</option>
          <option value="inCorso">In corso (bozza o emessa)</option>
          {Object.entries(STATI_EO).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
        </select>
        {(cerca||filtroStato||soloMie||filtroClienteId||filtroMese||filtroAnno) && (
          <button onClick={()=>{setCerca("");setFiltroStato("");setSoloMie(false);setFiltroClienteId(null);setFiltroClienteNome("");setFiltroMese("");setFiltroAnno("");}}
            style={{padding:"6px 12px",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",fontSize:12,cursor:"pointer"}}>
            × Reset
          </button>
        )}
      </div>

      {filtrate.length===0 ? (
        <div style={{textAlign:"center",padding:60,color:"#9ca3af"}}>
          <div style={{fontSize:48,marginBottom:12,opacity:.15}}>📐</div>
          <div style={{fontSize:14,fontWeight:600}}>Nessuna elaborazione offerta</div>
          <div style={{fontSize:12,marginTop:4}}>Crea la prima elaborazione, per un cliente esistente o per uno nuovo</div>
        </div>
      ) : (
        <div>
          {filtrate.map(eo => {
            const stato = STATI_EO[eo.stato]||STATI_EO.bozza;
            const totaliRiga = eo.stato==="finalizzata" ? calcolaTotaliEO(eo) : null;
            return (
              <div key={eo.id} onClick={()=>setEditEO(eo)}
                style={{border:"1px solid #e5e7eb",borderRadius:10,padding:"12px 16px",marginBottom:10,background:"#fff",display:"flex",alignItems:"center",gap:12,cursor:"pointer"}}>
                <div style={{fontFamily:"monospace",fontWeight:800,color:"#d97706",fontSize:15,flexShrink:0}}>{eo.numero||"—"}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:"#111827",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{eo.oggetto}</div>
                  <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>{eo.clienteNome||"—"} · {eo.dataEmissione?eo.dataEmissione.split("-").reverse().join("/"):"—"} · {eo.compilatoreNome}</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0,minWidth:90}}>
                  <div style={{fontSize:11,color:"#9ca3af"}}>Totale offerta</div>
                  <div style={{fontSize:16,fontWeight:800,color:"#059669"}}>€{fmt(eo.totaleGenerale)}</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0,minWidth:80}}>
                  <div style={{fontSize:11,color:"#9ca3af"}}>Margine</div>
                  <div style={{fontSize:14,fontWeight:700,color:totaliRiga?"#d97706":"#d1d5db"}}>{totaliRiga?`€${fmt(totaliRiga.totaleMargini)}`:"—"}</div>
                </div>
                <span style={{fontSize:11,padding:"3px 10px",borderRadius:10,background:stato.bg,color:stato.col,fontWeight:700,flexShrink:0}}>{stato.label}</span>
                <button onClick={e=>{e.stopPropagation();elimina(eo.id);}} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5",padding:4}}>
                  <Trash2 size={14}/>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
